import React, { useRef, useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useCccdScanner, PHASE_LABELS } from '../hooks/useCccdScanner';
import { detectCccdFromBlob } from '../services/cccdDetect';
import { detectDocumentApi, convertDvhcTextApi, searchDvhcApi, generateIdPhotoApi, updatePendingRecordApi, type DocTypeHint } from '../services/trungTamApi';
import DvhcAutocomplete from './DvhcAutocomplete';
import ZoomableImage from './ZoomableImage';
import ZoomOverlay from './ZoomOverlay';
import PortraitUploader from './PortraitUploader';
import TeacherAutocomplete from './TeacherAutocomplete';
import type { CccdScanResult, ScanMethod, BatchItem, DocDetectResult, ExtraImage, DocType } from '../types';

type ScanPhase = 'idle' | 'scanning' | 'qr-found' | 'ocr-running' | 'done' | 'error';

const STORAGE_KEY_SINGLE = 'tt_single_result';
const STORAGE_KEY_BATCH = 'tt_batch_items';
const STORAGE_KEY_BATCH_IDX = 'tt_batch_index';

type Props = {
  onSubmit: (data: CccdScanResult & { scanMethod: ScanMethod; pendingRecordId?: number }) => Promise<boolean>;
  batchReviewItems?: BatchItem[];
  onBatchReviewClear?: () => void;
  submitLabel?: string;
  submitIcon?: string;
  /** Callback to add files to the batch queue (parent-managed).
   *  Each invocation creates ONE record (1 student) — groups multiple
   *  files under a single QueueRecord. */
  onAddToQueue?: (files: FileList | File[]) => void;
  /** Richer variant of onAddToQueue — each file carries the operator-picked
   *  type hint so the backend can skip auto-classification. Preferred when
   *  available; falls back to onAddToQueue when the host page hasn't wired
   *  the hinted path yet. */
  onAddToQueueWithHints?: (items: { file: File; hint: DocTypeHint }[]) => void;
  /** Callback for batch upload — each file becomes its OWN record
   *  (N students). Used by "Nhập theo lô CCCD" card. */
  onAddBatchFiles?: (files: File[]) => void;
  /** Total records currently in the queue (for UI hint). */
  queueCount?: number;
  /** Records currently detecting (for UI hint). */
  queuePendingCount?: number;
  /** Records successfully detected and ready to review. */
  queueDoneCount?: number;
  /** When true, render only the simplified 2-card dashboard (Thêm hồ sơ
   *  mới + Nhập theo lô CCCD). Full form layout is hidden — review
   *  happens in RecordDetailModal from the "Chờ lưu hồ sơ" tab. */
  simpleMode?: boolean;
  /** Whether the active course is a "nâng hạng" (rank-upgrade) course.
   *  When true, the staging panel REQUIRES at least one image hinted as
   *  GPLX before the operator can hit "Gửi" — upgrade records can't be
   *  saved without the prior driver's licence proof. */
  isNangHangCourse?: boolean;
  /** Hide the TeacherAutocomplete and skip teacherId validation. Used
   *  by the SupperTeacher submit page where the teacher is implicitly
   *  the current user (set server-side from req.user.id) — there's no
   *  pick-a-teacher choice. */
  teacherFieldHidden?: boolean;
  /** Render the form in read-only mode — submit/skip buttons hidden,
   *  field inputs displayed via InfoField's read-only branch (no
   *  onChange wiring). Used by RecordDetailModal when opening a
   *  non-pending submission for inspect-only viewing. */
  readOnlyMode?: boolean;
};

/** Editable field renderer for the zoom-overlay info panel (right-side
 *  verification column). Two-way bound — typing updates the outer form
 *  state so fixes made while zoomed sync back to the scanner view.
 *  When `onChange` is omitted, renders read-only.
 *
 *  On focus/hover, shows a floating preview tooltip above the input that
 *  displays the value in a LARGE non-bold sans font — makes Vietnamese
 *  diacritics (sắc/huyền/hỏi/ngã/nặng, ă/â, ô/ơ, ư, đ) obvious at a
 *  glance. Small bold labels on forms often hide diacritics; the preview
 *  blows them up so the operator can spot missing/wrong marks before
 *  saving. */
/** Tiny pill next to a field label showing how the value was extracted.
 *  Emerald = QR (authoritative), amber = OCR (AI guess), slate = manual
 *  (operator typed). Rendered inline inside label row so the operator
 *  always sees trust level without hovering. */
const FieldSourceBadge: React.FC<{ source?: 'qr' | 'ocr' | 'manual' | null }> = ({ source }) => {
  if (!source) return null;
  if (source === 'qr') {
    return (
      <span className="tt-source-badge tt-source-badge--qr tt-source-badge--sm" title="Đọc từ mã QR — chính xác 100%">
        <i className="material-icons">verified</i> QR
      </span>
    );
  }
  if (source === 'ocr') {
    return (
      <span className="tt-source-badge tt-source-badge--ocr tt-source-badge--sm" title="Nhận dạng bằng AI — cần kiểm tra lại">
        <i className="material-icons">auto_awesome</i> OCR
      </span>
    );
  }
  return (
    <span className="tt-source-badge tt-source-badge--manual tt-source-badge--sm" title="Nhập tay">
      <i className="material-icons">edit_note</i> Tay
    </span>
  );
};

const InfoField: React.FC<{
  label: string;
  value?: string | null;
  mono?: boolean;
  onChange?: (next: string) => void;
  source?: 'qr' | 'ocr' | 'manual' | null;
  /** Logical group the field belongs to — cccd or gplx. Combined with
   *  `highlight` lets the panel pink-ring the matching fields when the
   *  operator opens the corresponding image in the zoom gallery. */
  group?: 'cccd' | 'gplx' | null;
  highlight?: boolean;
  /** Validation error message — when present the field shows a red
   *  border + shake + inline error text + the data-error-field attr so
   *  the parent form can scroll to the first invalid field. */
  error?: string | null;
  errorKey?: string;
}> = React.memo(({ label, value, mono, onChange, source, group, highlight, error, errorKey }) => {
  const readonly = !onChange;
  const [focused, setFocused] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const showPreview = (focused || hovered) && !!value;

  return (
    <div
      className={
        `tt-zoom-info-panel__field` +
        (group ? ` tt-zoom-info-panel__field--${group}` : '') +
        (highlight ? ' tt-zoom-info-panel__field--highlight' : '') +
        (error ? ' tt-zoom-info-panel__field--error' : '')
      }
      data-error-field={errorKey || undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {showPreview && (
        <div className={`tt-field-preview${mono ? ' tt-field-preview--mono' : ''}`} aria-hidden="true">
          <span className="tt-field-preview__label">{label}</span>
          <span className="tt-field-preview__value">{value}</span>
        </div>
      )}
      <span className="tt-zoom-info-panel__field-label">
        {label}
        <FieldSourceBadge source={source} />
      </span>
      {readonly ? (
        <span
          className={
            value
              ? `tt-zoom-info-panel__field-value${mono ? ' tt-zoom-info-panel__field-value--mono' : ''}`
              : 'tt-zoom-info-panel__field-value tt-zoom-info-panel__field-value--empty'
          }
        >
          {value || '— chưa có —'}
        </span>
      ) : (
        <input
          type="text"
          className={`tt-zoom-info-panel__field-input${mono ? ' tt-zoom-info-panel__field-input--mono' : ''}`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="— chưa có —"
        />
      )}
      {error && (
        <span className="tt-zoom-info-panel__field-error">{error}</span>
      )}
    </div>
  );
});
InfoField.displayName = 'InfoField';

// Operator request: dates at the END of their respective sections — mắt
// quen nhìn số CCCD / họ tên trước, ngày cấp ở cuối tạo flow tự nhiên.
const FIELD_CONFIG = [
  { key: 'soCCCD', label: 'CCCD', icon: 'badge', full: false },
  { key: 'soCMT', label: 'CMT', icon: 'credit_card', full: false },
  { key: 'hoTen', label: 'Họ tên', icon: 'person', full: false },
  { key: 'ngaySinh', label: 'Ngày sinh', icon: 'cake', full: false },
  { key: 'gioiTinh', label: 'Giới tính', icon: 'wc', full: false },
  { key: 'diaChi', label: 'Địa chỉ (CCCD)', icon: 'home', full: true },
  { key: 'maDvhc', label: 'Đơn vị hành chính', icon: 'location_city', full: true },
  { key: 'ngayCap', label: 'Ngày cấp CCCD', icon: 'event', full: false },
] as const;

const GPLX_FIELD_CONFIG: readonly { key: string; label: string; icon: string; full: boolean; dropdown?: boolean }[] = [
  { key: 'soGPLX', label: 'Số GPLX', icon: 'directions_car', full: false },
  { key: 'hangGPLX', label: 'Hạng GPLX (cũ)', icon: 'class', full: false },
  { key: 'hangGPLXMoi', label: 'Hạng GPLX (mới)', icon: 'swap_horiz', full: false, dropdown: true },
  { key: 'ngayCapGPLX', label: 'Ngày cấp GPLX', icon: 'event', full: false },
  { key: 'ngayHetHanGPLX', label: 'Ngày hết hạn', icon: 'event_busy', full: false },
  { key: 'ngayTrungTuyenGPLX', label: 'Ngày trúng tuyển', icon: 'emoji_events', full: false },
];

/**
 * Derive years-of-driving from "ngày trúng tuyển" (exam pass date).
 * Operator requested: auto-display SỐ NĂM LÁI XE next to ngayTrungTuyen,
 * red-flag if < 3 years (common threshold for "new driver" insurance /
 * quy định chạy thử). Read-only, recomputed whenever the operator
 * tweaks ngayTrungTuyen.
 *
 * Parses DD/MM/YYYY (with / . or - separators). Returns:
 *   null                                    — unparseable / future / empty
 *   { years: number, warning: boolean }     — years = fractional years,
 *                                              warning = years < 3
 */
const calcDrivingYears = (ngayTrungTuyen: string | null | undefined): { years: number; warning: boolean } | null => {
  if (!ngayTrungTuyen) return null;
  const m = String(ngayTrungTuyen).trim().match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (!m) return null;
  const d = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  let y = parseInt(m[3], 10);
  if (y < 100) y += y < 50 ? 2000 : 1900;
  if (!d || !mo || y < 1970 || y > 2100) return null;
  const passDate = new Date(y, mo - 1, d);
  if (Number.isNaN(passDate.getTime())) return null;
  const now = new Date();
  if (passDate > now) return null;
  const diffMs = now.getTime() - passDate.getTime();
  const years = diffMs / (365.25 * 24 * 60 * 60 * 1000);
  return { years, warning: years < 3 };
};

/** Old GPLX class → New GPLX class mapping (Luật TTATGT 2024) */
const HANG_GPLX_MAP: { old: string; new: string; label: string }[] = [
  { old: 'A01', new: 'A.01', label: 'A.01 (NKT)' },
  { old: 'A02', new: 'A.02', label: 'A.02' },
  { old: 'A1', new: 'A.03', label: 'A.03' },
  { old: 'A11', new: 'A11', label: 'A11 (NKT)' },
  { old: 'A1m', new: 'A1m', label: 'A1m' },
  { old: 'A2', new: 'Am', label: 'Am' },
  { old: 'A3', new: 'B1m', label: 'B1m' },
  { old: 'A4', new: 'A4', label: 'A4' },
  { old: 'Am', new: 'Am', label: 'Am' },
  { old: 'B1', new: 'B', label: 'B' },
  { old: 'B11', new: 'B.01', label: 'B.01 (STĐ)' },
  { old: 'B12', new: 'B.02', label: 'B.02 (STĐ TT)' },
  { old: 'B13', new: 'B.03', label: 'B.03 (STĐ TP)' },
  { old: 'B14', new: 'B.04', label: 'B.04 (STĐ CP)' },
  { old: 'B15', new: 'B.05', label: 'B.05 (STĐ NKT)' },
  { old: 'B2', new: 'B', label: 'B' },
  { old: 'C', new: 'Cm', label: 'Cm' },
  { old: 'C1', new: 'C1', label: 'C1' },
  { old: 'C1E', new: 'C1E', label: 'C1E' },
  { old: 'D', new: 'D2', label: 'D2' },
  { old: 'D1', new: 'D1', label: 'D1' },
  { old: 'D1E', new: 'D1E', label: 'D1E' },
  { old: 'E', new: 'Dm', label: 'Dm' },
  { old: 'FB2', new: 'BE', label: 'BE' },
  { old: 'FC', new: 'CE', label: 'CE' },
  { old: 'FD', new: 'D2E', label: 'D2E' },
  { old: 'FE', new: 'DE', label: 'DE' },
];

/** All unique new classes for dropdown */
const NEW_HANG_OPTIONS = [...new Map(HANG_GPLX_MAP.map(h => [h.new, h])).values()];

/** Convert old hang to new hang */
const convertHangGPLX = (oldHang: string): string => {
  const normalized = oldHang.trim().toUpperCase();
  const found = HANG_GPLX_MAP.find(h => h.old.toUpperCase() === normalized);
  return found ? found.new : oldHang;
};

/** Map detected docType to storage docType */
const toDocType = (dt: string): DocType => {
  if (dt === 'cccd') return 'cccd';
  if (dt === 'gplx') return 'gplx';
  if (dt === 'health') return 'health';
  return 'other';
};

/** Merge doc detection result into an existing CccdScanResult */
const mergeDocResult = (prev: CccdScanResult, det: DocDetectResult, imageBase64: string): CccdScanResult => {
  // Add label to distinguish front/back sides
  const sideLabel = det.docSide === 'back' ? `${toDocType(det.docType)}_back`
                  : det.docSide === 'front' ? `${toDocType(det.docType)}_front`
                  : undefined;
  const extra: ExtraImage = { base64: imageBase64, docType: toDocType(det.docType), label: sideLabel };
  const next = { ...prev, extraImages: [...prev.extraImages, extra] };

  if (det.docType === 'cccd' && det.cccd) {
    const c = det.cccd;
    if (c.soCCCD && !next.soCCCD) next.soCCCD = c.soCCCD;
    if (c.soCMT && !next.soCMT) next.soCMT = c.soCMT;
    if (c.hoTen && !next.hoTen) next.hoTen = c.hoTen.toUpperCase();
    if (c.ngaySinh && !next.ngaySinh) next.ngaySinh = c.ngaySinh;
    if (c.gioiTinh && !next.gioiTinh) next.gioiTinh = c.gioiTinh;
    // diaChi = NƠI THƯỜNG TRÚ — STRICT. Only from CCCD's noiThuongTru
    // (old card front) or noiCuTru (new Căn cước back). NEVER from
    // queQuan, noiDangKyKhaiSinh, or any GPLX address field.
    if ((c.noiThuongTru || c.noiCuTru) && !next.diaChi) {
      next.diaChi = c.noiThuongTru || c.noiCuTru;
    }
    // ngayCap: prioritize back side, allow override if docSide='back'
    if (c.ngayCap) {
      if (!next.ngayCap || det.docSide === 'back') next.ngayCap = c.ngayCap;
    }
  } else if (det.docType === 'gplx' && det.gplx) {
    const g = det.gplx;
    if (g.soGPLX) next.soGPLX = g.soGPLX;
    if (g.hang) {
      next.hangGPLX = g.hang;
      next.hangGPLXMoi = convertHangGPLX(g.hang);
    }
    if (g.ngayCap) next.ngayCapGPLX = g.ngayCap;
    if (g.noiCap) next.noiCapGPLX = g.noiCap;
    if (g.donViCap) next.donViCapGPLX = g.donViCap;
    if (g.ngayTrungTuyen) next.ngayTrungTuyenGPLX = g.ngayTrungTuyen;
    if (g.ngayHetHan) next.ngayHetHanGPLX = g.ngayHetHan;
    // GPLX may also have student name/DOB — fill if missing
    if (g.hoTen && !next.hoTen) next.hoTen = g.hoTen.toUpperCase();
    if (g.ngaySinh && !next.ngaySinh) next.ngaySinh = g.ngaySinh;
  } else if (det.docType === 'portrait' || det.portrait) {
    // Portrait photo → becomes the separate "ảnh thẻ học viên" slot, NOT
    // the CCCD avatar. The CCCD avatar is the source of truth for face
    // matching and must never be overwritten by an upload. Keep the
    // portrait base64 as-is (stripping any data URL prefix is done later
    // by the face service). Clear any GPLX-style extra-image entry so
    // the same file doesn't show twice.
    const cleanBase64 = imageBase64.includes(',')
      ? imageBase64.split(',', 2)[1]
      : imageBase64;
    next.portraitBase64 = cleanBase64;
    next.extraImages = next.extraImages.filter(e => e.base64 !== imageBase64);
  }

  return next;
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });

const TtScannerForm: React.FC<Props> = ({
  onSubmit,
  batchReviewItems,
  onBatchReviewClear,
  submitLabel = 'Lưu hồ sơ',
  submitIcon = 'save',
  onAddToQueue,
  onAddToQueueWithHints,
  onAddBatchFiles,
  queueCount = 0,
  queuePendingCount = 0,
  queueDoneCount = 0,
  simpleMode = false,
  isNangHangCourse = false,
  teacherFieldHidden = false,
  readOnlyMode = false,
}) => {
  const {
    result, phase, scanMethod,
    reset: rawReset, scanFromFile, setResult, setScanMethod,
  } = useCccdScanner();

  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY_SINGLE);
    rawReset();
  }, [rawReset]);

  // Camera mode removed — kept only "upload" (drag & drop multi-file)
  // and "manual". Default is upload so the dropzone is visible immediately.
  const [inputMode, setInputMode] = useState<'upload' | 'manual'>('upload');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const avatarFileRef = useRef<HTMLInputElement | null>(null);
  const extraFileRef = useRef<HTMLInputElement | null>(null);

  // When this form is mounted inside the review modal (batchReviewItems
  // passed in), init from those props DIRECTLY — do not hydrate from
  // localStorage. Reason: the review modal remounts on key change, but
  // React child effects (which were supposed to clear localStorage on
  // modal open) run AFTER our useState initializer. The result was the
  // operator clicking row B while row A was stale in localStorage → useState
  // initializer read A's leftovers → appended B → first render showed A's
  // data until a second effect cycle flushed it. Short-circuiting here
  // prevents that stale flash entirely.
  const isReviewMount = !!batchReviewItems && batchReviewItems.length > 0;
  const [batchItems, setBatchItems] = useState<BatchItem[]>(() => {
    if (isReviewMount) {
      // Tag with a batchId so the "consume batchReviewItems" effect below
      // doesn't re-append them after mount.
      return batchReviewItems!.map(item => ({ ...item, batchId: 0 }));
    }
    try {
      const saved = localStorage.getItem(STORAGE_KEY_BATCH);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [batchIndex, setBatchIndex] = useState(() => {
    if (isReviewMount) return 0;
    try { return Number(localStorage.getItem(STORAGE_KEY_BATCH_IDX) || 0); } catch { return 0; }
  });
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchDone, setBatchDone] = useState(0);

  // Queue input — operator picks files for 1 student at a time; files flow
  // up via onAddToQueue prop to the parent-managed queue (detection runs
  // silently in background while the operator continues adding records).
  const queueFileRef = useRef<HTMLInputElement | null>(null);

  // Mobile multi-capture staging. The native `<input capture>` only returns
  // one photo per invocation, so we accumulate each shot in `stagingItems`,
  // let the operator preview/delete, then submit all photos as ONE queue
  // record when they press "Gửi X ảnh". Each item carries a docType hint
  // (picked via the pill row above the capture buttons) so the backend can
  // skip its auto-classification step — massive speed win.
  // `id` is a stable per-item identity that survives re-renders and
  // re-tagging. Used as React key for the thumb + select so the DOM
  // node is reused across uploads — without it, every new file caused
  // ALL existing thumbs to remount (because the previous key strategy
  // was the object URL, and we used to regenerate URLs for every item
  // on every staging change). The remount made iOS Safari's <select>
  // briefly disagree with React's controlled value, surfacing as
  // "portrait got re-tagged GPLX" after the operator's third upload.
  type StagingItem = { id: string; file: File; hint: DocTypeHint };
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const [stagingItems, setStagingItems] = useState<StagingItem[]>([]);
  const [stagingOpen, setStagingOpen] = useState(false);
  // URL cache keyed by stable item id. Existing items keep their URL
  // when stagingItems changes — only newly added items get a fresh
  // URL, and removed items get their URL revoked.
  const stagingThumbsRef = useRef<Map<string, string>>(new Map());
  const [, forceThumbsRerender] = useState(0);
  const [activeHint, setActiveHint] = useState<DocTypeHint>('cccd');
  // Hints currently flashing red (over-limit feedback). Cleared by timer.
  const [flashingHints, setFlashingHints] = useState<Set<DocTypeHint>>(() => new Set());

  // Maintain object URLs without rebuilding the whole map on every
  // staging change. New items → create URL; removed items → revoke.
  // Existing items keep the same URL → React key stable → no remount.
  useEffect(() => {
    const map = stagingThumbsRef.current;
    const seen = new Set<string>();
    let dirty = false;
    for (const it of stagingItems) {
      seen.add(it.id);
      if (!map.has(it.id)) {
        map.set(it.id, URL.createObjectURL(it.file));
        dirty = true;
      }
    }
    for (const id of Array.from(map.keys())) {
      if (!seen.has(id)) {
        URL.revokeObjectURL(map.get(id)!);
        map.delete(id);
        dirty = true;
      }
    }
    if (dirty) forceThumbsRerender((t) => t + 1);
  }, [stagingItems]);

  // Cleanup all URLs on unmount. Doesn't run on staging change because
  // we manage URLs incrementally inside the effect above — this is the
  // last-line revoke for whatever URLs survive until unmount.
  useEffect(() => {
    const map = stagingThumbsRef.current;
    return () => {
      for (const url of map.values()) URL.revokeObjectURL(url);
      map.clear();
    };
  }, []);

  // Hover-to-zoom tooltip state — when any CCCD field is hovered, show a
  // large preview of the CCCD source image as a fixed-position floating
  // panel so operator can visually verify the field value.
  // hoveredZoom removed — the broken CCCD image tooltip it drove has been
  // replaced by per-field .tt-field-preview magnifiers in InfoField.

  // Refs for the batch strip — auto-scroll active thumb into view + drag-to-scroll
  const batchStripRef = useRef<HTMLDivElement | null>(null);
  const activeThumbRef = useRef<HTMLButtonElement | null>(null);
  const dragState = useRef<{ active: boolean; startX: number; startScroll: number; moved: boolean }>({
    active: false, startX: 0, startScroll: 0, moved: false,
  });

  const isBatchMode = batchItems.length > 0;
  const currentBatch = isBatchMode ? batchItems[batchIndex] : null;
  const batchSuccessCount = batchItems.filter(b => b.status === 'done').length;
  const batchErrorCount = batchItems.filter(b => b.status === 'error').length;

  // Use batch result as current display when in batch mode
  const displayResult = isBatchMode ? currentBatch?.result ?? null : result;
  const displaySource = isBatchMode
    ? currentBatch?.result?.sourceImageBase64 || currentBatch?.preview || null
    : result?.sourceImageBase64 ?? null;

  // Scan lifecycle helper — `busy` means the OCR pipeline is actively running,
  // during which we hide the dropzone (prevents double-drop). When the scan
  // lands in `done` we SHOW the left column again so NVTT can compare the
  // detected images/fields side-by-side via the doc tabs below.
  //
  // `uploading` flips true the moment the user picks / drops a file — without
  // it there's a short window before the useCccdScanner phase transitions to
  // "scanning" where the dropzone would still be visible and the user could
  // accidentally drop again.
  const [uploading, setUploading] = useState(false);
  const isScanBusy = uploading || phase === 'scanning' || phase === 'qr-found' || phase === 'ocr-running';

  // Doc tabs — CCCD | GPLX | Tổng quan — pick which document to focus on.
  // Layout contract: left col shows the zoomable image for the active doc,
  // right col shows the matching field group. "Tổng quan" shows everything.
  type DocTab = 'cccd' | 'gplx' | 'all';
  const [activeDocTab, setActiveDocTab] = useState<DocTab>('cccd');

  // Any GPLX evidence — either an image uploaded with docType=gplx or OCR
  // already filled in the GPLX number/class. When absent, hide the GPLX tab
  // and the GPLX field block entirely so NVTT isn't staring at empty rows.
  const gplxExtra = (displayResult?.extraImages || []).find(e => e.docType === 'gplx');
  const gplxImage = gplxExtra?.base64 || null;
  const hasGplxData = !!(
    gplxImage ||
    displayResult?.soGPLX ||
    displayResult?.hangGPLX ||
    displayResult?.hangGPLXMoi ||
    displayResult?.ngayCapGPLX ||
    displayResult?.ngayHetHanGPLX ||
    displayResult?.ngayTrungTuyenGPLX
  );

  // When GPLX evidence disappears (e.g. user removed the extra image), bounce
  // back to CCCD so the UI doesn't get stuck on an empty tab.
  useEffect(() => {
    if (!hasGplxData && activeDocTab === 'gplx') setActiveDocTab('cccd');
  }, [hasGplxData, activeDocTab]);

  // Field-section visibility driven by active tab. In manual/batch modes we
  // always show everything so NVTT can fill values regardless of tab state.
  const showCccdFields = isBatchMode || !hasGplxData || activeDocTab === 'cccd' || activeDocTab === 'all';
  const showGplxFields = isBatchMode || activeDocTab === 'gplx' || activeDocTab === 'all';

  // DVHC display text — purely derived from the pre-resolved record.
  // Address conversion now happens out-of-band via the bulk convert button,
  // so the scanner form never calls diachi.io inline. If the user manually
  // picked a ward from the autocomplete (maDvhc + dvhcTenDayDu both set),
  // we reflect it; otherwise the field is left blank with a hint.
  const [dvhcDisplayText, setDvhcDisplayText] = useState('');
  // Validation error map keyed by field name (CccdScanResult key + 'teacherId').
  // Populated by validateAndPrepareRecord on save click; cleared per field
  // as the operator types so red borders disappear in real time.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const clearFieldError = (key: string) => {
    setFieldErrors(prev => (prev[key] ? { ...prev, [key]: null } : prev));
  };
  useEffect(() => {
    setDvhcDisplayText(displayResult?.dvhcTenDayDu || '');
  }, [displayResult?.maDvhc, displayResult?.dvhcTenDayDu]);

  // Restore single-mode result from localStorage on first mount
  useEffect(() => {
    if (isBatchMode) return;
    try {
      const saved = localStorage.getItem(STORAGE_KEY_SINGLE);
      if (!saved) return;
      const { result: r, scanMethod: sm } = JSON.parse(saved);
      if (r?.soCCCD) { setResult(r); setScanMethod(sm || 'ocr'); }
    } catch { /* ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist single-mode result to localStorage whenever it changes
  useEffect(() => {
    if (isBatchMode) return;
    if (result?.soCCCD) {
      try { localStorage.setItem(STORAGE_KEY_SINGLE, JSON.stringify({ result, scanMethod })); } catch { /* ignore */ }
    } else {
      localStorage.removeItem(STORAGE_KEY_SINGLE);
    }
  }, [result, scanMethod, isBatchMode]);

  // ── Auto-convert DVHC ─────────────────────────────────────────────
  // STRICT lookup — "không tìm ra thì để trống, KHÔNG được trả về kết
  // quả sai". Flow:
  //   1. Split diaChi by comma → trim → drop empties.
  //   2. ≥3 parts (old 3-tier "xã, huyện, tỉnh") → keep last 3 → call
  //      the backend convert endpoint (diachi.io + local dvhc validation).
  //      If the server can't match confidently → fail with empty DVHC.
  //      Do NOT fall back to 2-tier fuzzy lookup: old province names
  //      like "Bình Định" don't exist in the post-reform canonical
  //      table, so a ward-only match would return the wrong province.
  //   3. Exactly 2 parts (already new format "xã, tỉnh") → strict
  //      dictionary search: both ward AND province must appear in the
  //      matched row's tenDayDu. No "first ward found" fallback.
  //   4. <2 parts → can't resolve at all.
  const [dvhcConverting, setDvhcConverting] = useState(false);
  const [dvhcConvertError, setDvhcConvertError] = useState<string | null>(null);
  const lastConvertedDiaChi = useRef<string>('');

  useEffect(() => {
    const rawDiaChi = (displayResult?.diaChi || '').trim();
    if (rawDiaChi.length < 6) { setDvhcConvertError(null); return; }
    if (displayResult?.maDvhc && displayResult?.dvhcTenDayDu) return;
    if (lastConvertedDiaChi.current === rawDiaChi) return;

    const timer = setTimeout(async () => {
      lastConvertedDiaChi.current = rawDiaChi;
      setDvhcConverting(true);
      setDvhcConvertError(null);

      const parts = rawDiaChi.split(',').map(s => s.trim()).filter(Boolean);
      const applyPatch = (maDvhc: string, tenDayDu: string) => {
        const patch = { maDvhc, dvhcTenDayDu: tenDayDu, diaChiMoi: tenDayDu } as Record<string, string>;
        setDvhcDisplayText(tenDayDu);
        if (isBatchMode) {
          setBatchItems(prev => prev.map((item, idx) =>
            idx === batchIndex && item.result
              ? { ...item, result: { ...item.result, ...patch } }
              : item
          ));
        } else {
          // Functional setState so we always merge into the LATEST
          // result — avoids stale closure capture if OCR finished
          // between the effect setup and the debounced timer firing.
          setResult(prev => prev ? { ...prev, ...patch } : prev);
        }
      };

      // Strict 2-tier dictionary lookup used only for inputs that are
      // already in new format ("<xã>, <tỉnh>"). Requires an entry whose
      // tenDayDu contains BOTH the ward name and the province name.
      const strictTwoLevelLookup = async (ward: string, province: string): Promise<boolean> => {
        const normalize = (s: string) =>
          s.toLowerCase()
            .replace(/^(xã|phường|thị trấn|thị xã|huyện|quận|tỉnh|thành phố|tp\.?)\s+/i, '')
            .replace(/\s+/g, ' ')
            .trim();
        const nWard = normalize(ward);
        const nProv = normalize(province);
        if (!nWard || !nProv) return false;

        try {
          const resp = await searchDvhcApi(ward, 50);
          if (resp.EC !== 0 || !Array.isArray(resp.DT) || resp.DT.length === 0) return false;

          const exact = resp.DT.find(d => {
            const full = normalize(d.tenDayDu);
            return full.includes(nWard) && full.includes(nProv);
          });
          if (!exact) return false; // STRICT — no fallback on province-only.
          applyPatch(exact.maDvhc, exact.tenDayDu);
          return true;
        } catch {
          return false;
        }
      };

      try {
        // ── Case A: ≥3 parts → convert via backend (no fallback) ────
        if (parts.length >= 3) {
          const last3 = parts.slice(-3);
          const joined = last3.join(', ');
          try {
            const resp = await convertDvhcTextApi(joined);
            if (resp.EC === 0 && resp.DT && resp.DT.maDvhc) {
              applyPatch(resp.DT.maDvhc, resp.DT.tenDayDu);
              return;
            }
            setDvhcConvertError(resp.EM || 'Không tìm được đơn vị hành chính phù hợp');
          } catch (err: unknown) {
            const e = err as { response?: { data?: { EM?: string } } };
            setDvhcConvertError(e?.response?.data?.EM || 'Lỗi khi chuyển đổi ĐVHC');
          }
          return;
        }

        // ── Case B: exactly 2 parts → strict dictionary search ──────
        if (parts.length === 2) {
          const [ward, province] = parts;
          const found = await strictTwoLevelLookup(ward, province);
          if (!found) {
            setDvhcConvertError(`Không tìm thấy "${ward}, ${province}" trong danh mục ĐVHC`);
          }
          return;
        }

        // ── Case C: less than 2 parts → cannot resolve ──────────────
        setDvhcConvertError('Địa chỉ CCCD không đủ cấp để tra cứu (cần ít nhất xã + tỉnh)');
      } finally {
        setDvhcConverting(false);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayResult?.diaChi, displayResult?.maDvhc, displayResult?.dvhcTenDayDu, isBatchMode, batchIndex]);

  // ── Auto face match ───────────────────────────────────────────────
  // Whenever we have BOTH a CCCD crop and an uploaded portrait but no
  // matching score yet, fire a background compare against the Python
  // service and write the result back into state. Runs for both single
  // and batch modes. Guard with an in-memory set of already-compared
  // Face-match auto-compare removed per operator request: (1) CCCD crop
  // is frequently missing (poor scan / obscured face on thẻ cũ), which
  // would leave the field in a limbo "chưa so sánh" state forever;
  // (2) the score was never acted on — operators just closed the toast.
  // Portrait upload now proceeds even when `avatarBase64` is null; the
  // portrait stands on its own as the student's identity photo.

  // Persist batch items and index to localStorage whenever they change
  useEffect(() => {
    if (!isBatchMode) return;
    try { localStorage.setItem(STORAGE_KEY_BATCH, JSON.stringify(batchItems)); } catch { /* ignore */ }
  }, [batchItems, isBatchMode]);

  useEffect(() => {
    if (!isBatchMode) return;
    localStorage.setItem(STORAGE_KEY_BATCH_IDX, String(batchIndex));
  }, [batchIndex, isBatchMode]);

  // Smoothly scroll the active thumbnail into view whenever batchIndex changes.
  // Using manual centering instead of scrollIntoView so the page itself
  // doesn't get pulled — only the strip's horizontal scroll moves.
  useEffect(() => {
    const strip = batchStripRef.current;
    const thumb = activeThumbRef.current;
    if (!strip || !thumb) return;

    const stripRect = strip.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    const offset = (thumbRect.left - stripRect.left)
      - (stripRect.width / 2)
      + (thumbRect.width / 2);
    strip.scrollTo({ left: strip.scrollLeft + offset, behavior: 'smooth' });
  }, [batchIndex, batchItems.length]);

  // Drag-to-scroll handlers. Capture is delayed until the user actually moves
  // past the threshold — so a simple tap/click still routes to the thumbnail
  // button normally instead of being intercepted by the strip.
  const DRAG_THRESHOLD = 5;

  const handleStripPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const strip = batchStripRef.current;
    if (!strip) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragState.current = {
      active: true,
      startX: e.clientX,
      startScroll: strip.scrollLeft,
      moved: false,
    };
  }, []);

  const handleStripPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const strip = batchStripRef.current;
    if (!strip || !dragState.current.active) return;
    const dx = e.clientX - dragState.current.startX;

    // Once movement exceeds the threshold, claim pointer capture so the
    // browser stops generating a click on release.
    if (!dragState.current.moved && Math.abs(dx) > DRAG_THRESHOLD) {
      dragState.current.moved = true;
      try { strip.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    }

    if (dragState.current.moved) {
      strip.scrollLeft = dragState.current.startScroll - dx;
      e.preventDefault();
    }
  }, []);

  const handleStripPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const strip = batchStripRef.current;
    if (!strip) return;
    if (strip.hasPointerCapture(e.pointerId)) {
      try { strip.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    }
    dragState.current.active = false;
    // moved flag is read by the click handler that fires immediately after
    // pointerup on the same element; reset it on the next tick.
    setTimeout(() => { dragState.current.moved = false; }, 0);
  }, []);

  // Block the click only when an actual drag occurred (preserve normal taps)
  const handleThumbClick = useCallback((idx: number) => {
    if (dragState.current.moved) return;
    setBatchIndex(idx);
  }, []);

  // Hover preview tooltip for thumbnails — shows the cropped avatar (or
  // fallback preview), full name and CCCD so the user can identify the
  // record without clicking through.
  const [thumbHover, setThumbHover] = useState<{ idx: number; x: number; y: number } | null>(null);
  const thumbHoverTimer = useRef<ReturnType<typeof setTimeout>>();

  const handleThumbEnter = useCallback((idx: number, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top - 8;
    clearTimeout(thumbHoverTimer.current);
    thumbHoverTimer.current = setTimeout(() => setThumbHover({ idx, x, y }), 220);
  }, []);

  const handleThumbLeave = useCallback(() => {
    clearTimeout(thumbHoverTimer.current);
    setThumbHover(null);
  }, []);

  // Track latest batch ID for selective cancel
  const batchIdCounter = useRef(0);
  const [latestBatchId, setLatestBatchId] = useState<number | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Receive batch items from sidebar "Xem chi tiết" — append to existing list.
  // Skipped entirely when we're mounted with `batchReviewItems` already set
  // (review-modal mount path) — the initial useState already consumed them.
  // Re-appending here on the same render would double the list and the
  // operator would see the previous record's image/fields flash until the
  // dedup cycle finished.
  const initialReviewMountRef = useRef(isReviewMount);
  useEffect(() => {
    if (initialReviewMountRef.current) {
      // First effect fire after the review-mount — already consumed by
      // useState initialiser. Skip to avoid double-append.
      initialReviewMountRef.current = false;
      setLatestBatchId(0);
      setBatchProcessing(false);
      setInputMode('upload');
      onBatchReviewClear?.();
      return;
    }
    if (batchReviewItems && batchReviewItems.length > 0) {
      const newId = ++batchIdCounter.current;
      const tagged = batchReviewItems.map(item => ({ ...item, batchId: newId }));
      setBatchItems(prev => [...prev, ...tagged]);
      setLatestBatchId(newId);
      setBatchProcessing(false);
      setInputMode('upload');
      onBatchReviewClear?.();
    }
  }, [batchReviewItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Auto-save (review-mount only) ─────────────────────────────────
  // Operator edits in the "Chờ lưu hồ sơ" review popup were local-only
  // until they clicked Save — an F5 during editing dropped everything.
  // Every field change now schedules a debounced PUT to the pending
  // record so data survives refresh. The debounce window coalesces
  // rapid typing into a single request.
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const autoSavePendingRef = useRef<Record<string, unknown>>({});

  const flushAutoSave = useCallback(async () => {
    if (!isReviewMount) return;
    const rec = currentBatch;
    if (!rec?.pendingRecordId) return;
    const diff = autoSavePendingRef.current;
    if (!Object.keys(diff).length) return;
    autoSavePendingRef.current = {};
    setAutoSaveStatus('saving');
    try {
      await updatePendingRecordApi(rec.pendingRecordId, diff);
      setAutoSaveStatus('saved');
    } catch {
      setAutoSaveStatus('error');
    }
  }, [isReviewMount, currentBatch]);

  // Flush on tab hide / unmount so the last-typed field never gets lost.
  useEffect(() => {
    if (!isReviewMount) return;
    const onHide = () => { void flushAutoSave(); };
    window.addEventListener('beforeunload', onHide);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('beforeunload', onHide);
      document.removeEventListener('visibilitychange', onHide);
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      // Fire-and-forget final flush on unmount; we don't await since
      // React doesn't let unmount cleanups be async anyway.
      void flushAutoSave();
    };
  }, [isReviewMount, flushAutoSave]);

  const scheduleAutoSave = useCallback((patch: Record<string, unknown>) => {
    if (!isReviewMount) return;
    Object.assign(autoSavePendingRef.current, patch);
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => { void flushAutoSave(); }, 700);
  }, [isReviewMount, flushAutoSave]);

  const handleFieldChange = (key: string, value: string) => {
    // Clear the field's validation error as soon as the operator starts
    // typing — red border disappears in real time, no need to re-click
    // save to refresh the error map.
    clearFieldError(key);
    // Auto-convert old hang → new hang when hangGPLX changes
    const extra: Record<string, string> = {};
    if (key === 'hangGPLX' && value) {
      extra.hangGPLXMoi = convertHangGPLX(value);
    }
    // When the user edits diaChi, drop the cached DVHC so the auto-resolve
    // effects pick up the new address. Don't clear when picking from
    // DvhcAutocomplete (key === 'maDvhc' goes through here separately).
    if (key === 'diaChi') {
      extra.maDvhc = '';
      extra.dvhcTenDayDu = '';
      extra.diaChiMoi = '';
      setDvhcDisplayText('');
    }

    if (isBatchMode && currentBatch?.result) {
      setBatchItems(prev => prev.map((item, idx) =>
        idx === batchIndex ? { ...item, result: { ...item.result!, [key]: value, ...extra } } : item
      ));
      // Schedule remote autosave for THIS patch only (not the whole record).
      scheduleAutoSave({ [key]: value, ...extra });
    } else if (!result) {
      const blank: CccdScanResult = {
        soCCCD: '', soCMT: '', hoTen: '', ngaySinh: '', gioiTinh: '', diaChi: '', diaChiMoi: '', ngayCap: '',
        avatarBase64: null, sourceImageBase64: null, detectedMethod: null, extraImages: [],
        soGPLX: '', hangGPLX: '', hangGPLXMoi: '', ngayCapGPLX: '', noiCapGPLX: '', donViCapGPLX: '', ngayTrungTuyenGPLX: '', ngayHetHanGPLX: '', maDvhc: '',
      };
      setResult({ ...blank, [key]: value, ...extra });
      setScanMethod('manual');
    } else {
      setResult({ ...result, [key]: value, ...extra });
    }
  };

  /** Wrap an onChange handler so it becomes undefined in readOnlyMode.
   *  InfoField renders the read-only branch (text instead of input) when
   *  no onChange is passed — that's how we make View modals visually
   *  inert without duplicating the field markup. */
  const roChange = (cb: (v: string) => void): ((v: string) => void) | undefined =>
    readOnlyMode ? undefined : cb;

  const [submitting, setSubmitting] = useState(false);
  const [avatarZoom, setAvatarZoom] = useState(false);
  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null);
  // Which document is currently on-screen in the zoom overlay — lets us
  // pink-highlight the matching form fields on the right panel so the
  // operator's eye doesn't have to hunt for "which field goes with this
  // image". Null when the overlay is closed or the image is not a
  // CCCD/GPLX (e.g. portrait / other extras).
  const [zoomActiveGroup, setZoomActiveGroup] = useState<'cccd' | 'gplx' | null>(null);
  const [generatingIdPhoto, setGeneratingIdPhoto] = useState(false);

  // Magnify tooltip for long text
  const MAGNIFY_KEYS = new Set(['hoTen', 'diaChi']);
  const [tipText, setTipText] = useState('');
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });
  const [tipVisible, setTipVisible] = useState(false);
  const tipTimer = useRef<ReturnType<typeof setTimeout>>();

  const showTip = useCallback((e: React.MouseEvent, text: string) => {
    clearTimeout(tipTimer.current);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTipText(text);
    // Place the tooltip ABOVE the hovered field so the original text
    // underneath stays readable (and copyable). `top` is the field's
    // top edge; the render uses `translateY(-100%)` to lift the tooltip
    // entirely above that baseline with a small gap.
    setTipPos({ x: rect.left, y: rect.top - 8 });
    tipTimer.current = setTimeout(() => setTipVisible(true), 350);
  }, []);

  const hideTip = useCallback(() => {
    clearTimeout(tipTimer.current);
    setTipVisible(false);
  }, []);

  const stripPrefix = (s: string | null | undefined): string | null => {
    if (!s || !s.trim()) return null;
    return s.includes(',') ? s.split(',', 2)[1] : s;
  };

  /**
   * Client-side validation that mirrors the backend's required-field +
   * date-format rules. When ANY field is missing/invalid we populate the
   * fieldErrors map (red border on every offending field), show ONE
   * deduped toast, scroll to the first error, and abort the save —
   * backend remains the safety net but normal users never see a
   * server-side validation toast on top of the client-side one.
   */
  const validateAndPrepareRecord = (r: CccdScanResult): CccdScanResult | null => {
    const errors: Record<string, string> = {};
    const trimmed = (v: unknown) => (v == null ? '' : String(v).trim());

    const DATE_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const isBadDate = (raw: unknown): boolean => {
      const s = trimmed(raw);
      if (!s) return false;
      const m = s.match(DATE_RE);
      if (!m) return true;
      const d = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10);
      const y = parseInt(m[3], 10);
      if (mo < 1 || mo > 12 || y < 1900 || y > 2200) return true;
      const daysInMonth = new Date(y, mo, 0).getDate();
      if (d < 1 || d > daysInMonth) return true;
      return false;
    };

    // ─ Required CCCD personal fields ─
    if (!trimmed(r.soCCCD)) errors.soCCCD = 'Cần nhập số CCCD';
    if (!trimmed(r.hoTen)) errors.hoTen = 'Cần nhập họ tên';
    if (!trimmed(r.ngaySinh)) errors.ngaySinh = 'Cần nhập ngày sinh';
    if (!trimmed(r.gioiTinh)) errors.gioiTinh = 'Cần nhập giới tính';
    if (!trimmed(r.diaChi)) errors.diaChi = 'Cần nhập địa chỉ thường trú';
    if (!trimmed(r.ngayCap)) errors.ngayCap = 'Cần nhập ngày cấp CCCD';

    // ─ Teacher (carried inside r as r.teacherId via TeacherAutocomplete) ─
    // Skip when the teacher field is hidden — teacher portal sets the
    // teacher implicitly server-side, so the picker isn't shown and
    // validation doesn't apply.
    if (!teacherFieldHidden) {
      const tid = (r as { teacherId?: number | null }).teacherId;
      if (!tid) errors.teacherId = 'Cần chọn giáo viên phụ trách';
    }

    // ─ GPLX block — required when course is nâng-hạng OR any GPLX
    //   field has been touched (half-filled GPLX is "incomplete data") ─
    const anyGplx = trimmed(r.soGPLX) || trimmed(r.hangGPLX) || trimmed(r.hangGPLXMoi)
      || trimmed(r.ngayCapGPLX) || trimmed(r.ngayTrungTuyenGPLX) || trimmed(r.ngayHetHanGPLX);
    const requiresGplx = isNangHangCourse || !!anyGplx;
    if (requiresGplx) {
      if (!trimmed(r.soGPLX)) errors.soGPLX = 'Cần nhập số GPLX cũ';
      if (!trimmed(r.hangGPLX)) errors.hangGPLX = 'Cần nhập hạng GPLX cũ';
      // hangGPLXMoi is auto-derived from hangGPLX via convertHangGPLX —
      // skip explicit validation (would double-report the same root
      // issue: empty hangGPLX). On save we ensure hangGPLXMoi is filled
      // server-side via the derive call below.
      if (!trimmed(r.ngayCapGPLX)) errors.ngayCapGPLX = 'Cần nhập ngày cấp GPLX';
      if (!trimmed(r.ngayTrungTuyenGPLX)) errors.ngayTrungTuyenGPLX = 'Cần nhập ngày trúng tuyển';
      if (!trimmed(r.ngayHetHanGPLX)) errors.ngayHetHanGPLX = 'Cần nhập ngày hết hạn GPLX';
    }

    // ─ Date format DD/MM/YYYY ─
    if (!errors.ngaySinh && isBadDate(r.ngaySinh)) errors.ngaySinh = 'Sai định dạng (DD/MM/YYYY)';
    if (!errors.ngayCap && isBadDate(r.ngayCap)) errors.ngayCap = 'Sai định dạng (DD/MM/YYYY)';
    if (!errors.ngayCapGPLX && isBadDate(r.ngayCapGPLX)) errors.ngayCapGPLX = 'Sai định dạng (DD/MM/YYYY)';
    if (!errors.ngayTrungTuyenGPLX && isBadDate(r.ngayTrungTuyenGPLX)) errors.ngayTrungTuyenGPLX = 'Sai định dạng (DD/MM/YYYY)';
    if (!errors.ngayHetHanGPLX && isBadDate(r.ngayHetHanGPLX)) errors.ngayHetHanGPLX = 'Sai định dạng (DD/MM/YYYY)';

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      // Single toast (toastId dedupes against rapid clicks AND against
      // a server-side toast that shouldn't fire anymore now that the
      // client catches every BE rule first).
      toast.error(
        `Vui lòng điền đầy đủ thông tin (còn ${Object.keys(errors).length} trường thiếu).`,
        { toastId: 'tt-scanner-validation', autoClose: 4000 },
      );
      // Scroll to first error field & focus its input.
      const firstKey = Object.keys(errors)[0];
      requestAnimationFrame(() => {
        const node = document.querySelector(`[data-error-field="${firstKey}"]`) as HTMLElement | null;
        if (node) {
          node.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (node.querySelector('input,select,textarea') as HTMLElement | null)?.focus();
        }
      });
      return null;
    }
    setFieldErrors({});

    // ─ DVHC + portrait — separate UX (toast + confirm), not field-level ─
    const dvhcReady = (r.dvhcTenDayDu && r.dvhcTenDayDu.trim()) || (dvhcDisplayText && dvhcDisplayText.trim());
    if (!dvhcReady) {
      toast.error(
        'Phải có Đơn vị hành chính (cấp 2 mới) trước khi lưu. Hãy chờ chuyển đổi tự động hoặc chọn thủ công.',
        { toastId: 'tt-scanner-validation', autoClose: 4000 },
      );
      return null;
    }
    if (!r.portraitBase64?.trim()) {
      const ok = window.confirm(
        'Học viên chưa có ảnh thẻ riêng.\n\nBấm OK để lưu không có ảnh thẻ, hoặc Hủy để upload ảnh thẻ trước.',
      );
      if (!ok) return null;
    }
    return {
      ...r,
      avatarBase64: stripPrefix(r.avatarBase64) || r.avatarBase64,
      portraitBase64: stripPrefix(r.portraitBase64) || r.portraitBase64 || null,
      sourceImageBase64: stripPrefix(r.sourceImageBase64) || r.sourceImageBase64 || null,
      dvhcTenDayDu: r.dvhcTenDayDu || dvhcDisplayText || undefined,
      // Auto-derive new-class GPLX rank when operator only set the old
      // class — backend requires both. Falls back to the old value if
      // the rank isn't in our convert map.
      hangGPLXMoi: trimmed(r.hangGPLXMoi) ? r.hangGPLXMoi : convertHangGPLX(r.hangGPLX || ''),
    };
  };

  const handleSubmit = async () => {
    if (submitting) return;

    if (isBatchMode && currentBatch?.result) {
      const prepared = validateAndPrepareRecord(currentBatch.result);
      if (!prepared) return;
      setSubmitting(true);
      // Carry pendingRecordId through so parent can route to savePendingRecord
      // API (atomic: commit to tt_student_record + delete pending row).
      const ok = await onSubmit({
        ...prepared,
        scanMethod: prepared.detectedMethod || 'ocr',
        pendingRecordId: currentBatch.pendingRecordId,
      });
      setSubmitting(false);
      if (ok) {
        setBatchItems(prev => prev.filter((_, i) => i !== batchIndex));
        setBatchIndex(prev => Math.min(prev, Math.max(0, batchItems.length - 2)));
      }
      return;
    }
    if (!result) {
      toast.info('Chưa có dữ liệu hợp lệ để thêm.');
      return;
    }
    const prepared = validateAndPrepareRecord(result);
    if (!prepared) return;
    setSubmitting(true);
    const ok = await onSubmit({ ...prepared, scanMethod });
    setSubmitting(false);
    if (ok) {
      reset();
    }
  };

  const handleGenerateIdPhoto = async () => {
    if (generatingIdPhoto) return;
    const avatar = displayResult?.avatarBase64;
    if (!avatar) {
      toast.info('Chưa có ảnh CCCD để tạo ảnh thẻ.');
      return;
    }
    try {
      setGeneratingIdPhoto(true);
      const res = await fetch(avatar);
      const blob = await res.blob();
      const gender = (displayResult?.gioiTinh || '').toLowerCase().includes('nữ') ? 'female' : 'male';
      const data = await generateIdPhotoApi(blob, gender);
      if (data.EC === 0 && data.DT) {
        const generated = data.DT.startsWith('data:') ? data.DT : `data:image/jpeg;base64,${data.DT}`;
        if (isBatchMode) {
          setBatchItems(prev => prev.map((item, idx) =>
            idx === batchIndex && item.result
              ? { ...item, result: { ...item.result, portraitBase64: generated, faceMatchScore: null } }
              : item,
          ));
        } else {
          setResult(prev => (prev ? { ...prev, portraitBase64: generated, faceMatchScore: null } : prev));
        }
        toast.success('Đã tạo ảnh thẻ AI.');
      } else {
        toast.error(data.EM || 'Không tạo được ảnh thẻ.');
      }
    } catch (e) {
      toast.error('Lỗi tạo ảnh thẻ AI.');
    } finally {
      setGeneratingIdPhoto(false);
    }
  };

  const [detectingExtras, setDetectingExtras] = useState(false);

  /**
   * Shared pipeline for "Tải ảnh" upload/drop.
   *
   * Step 1: classify ALL files in parallel (no assumption about order).
   * Step 2: pick the primary CCCD file — the main image displayed at the top
   *         comes from whichever file is actually classified as CCCD, not from
   *         files[0]. This fixes the case where the user uploads GPLX first
   *         and CCCD second.
   * Step 3: run QR/OCR scan on the primary CCCD file only.
   * Step 4: merge the rest (portrait → avatar, gplx → fields, cccd → extras+fill,
   *         other → extras).
   */
  const processUploadedFiles = async (files: FileList) => {
    const prevPortrait = result?.portraitBase64 || null;
    clearBatch();
    reset();

    setDetectingExtras(true);

    // Step 1: classify every file in parallel via Mistral. One pass tells us
    // which file is the CCCD, which is GPLX, portrait, or other. Backend's
    // /api/file/qr/decode (called later by scanFromFile) will itself prefer
    // QR over OCR on the CCCD file — no need to pre-probe on the client.
    const detections = await Promise.all(
      Array.from(files).map(async (file) => {
        const [b64, detectResp] = await Promise.all([
          fileToBase64(file),
          detectDocumentApi(file).catch(() => null),
        ]);
        return { file, b64, detect: detectResp?.EC === 0 ? detectResp.DT : null };
      }),
    );

    // Prefer CCCD front side for primary scan (sets sourceImageBase64); fall back to any CCCD
    let cccdIdx = detections.findIndex((d) => d.detect?.docType === 'cccd' && d.detect?.docSide === 'front');
    if (cccdIdx < 0) cccdIdx = detections.findIndex((d) => d.detect?.docType === 'cccd');

    if (cccdIdx >= 0) {
      // scanFromFile hits /api/file/qr/decode — backend does QR + OCR in
      // parallel and returns whichever succeeds (QR wins if both).
      await scanFromFile(detections[cccdIdx].file);

      setResult((prev) => {
        if (!prev) return prev;
        let merged = { ...prev };
        for (let i = 0; i < detections.length; i += 1) {
          if (i === cccdIdx) continue;
          const { b64, detect } = detections[i];
          if (detect) {
            merged = mergeDocResult(merged, detect, b64);
          } else {
            merged = {
              ...merged,
              extraImages: [...merged.extraImages, { base64: b64, docType: 'other' as DocType }],
            };
          }
        }
        if (prevPortrait && !merged.portraitBase64) {
          merged = { ...merged, portraitBase64: prevPortrait, faceMatchScore: null };
        }
        return merged;
      });
    } else {
      // No CCCD uploaded — handle portrait/GPLX/other without running the
      // CCCD scanner (which would fail and show an error toast). Start from
      // a blank result and fold every file in via mergeDocResult so a
      // portrait becomes the avatar and a GPLX fills the GPLX fields.
      const blank: CccdScanResult = {
        soCCCD: '', soCMT: '', hoTen: '', ngaySinh: '', gioiTinh: '',
        diaChi: '', diaChiMoi: '', ngayCap: '',
        avatarBase64: null, sourceImageBase64: null, detectedMethod: null,
        extraImages: [],
        soGPLX: '', hangGPLX: '', hangGPLXMoi: '',
        ngayCapGPLX: '', noiCapGPLX: '', donViCapGPLX: '',
        ngayTrungTuyenGPLX: '', ngayHetHanGPLX: '', maDvhc: '',
      };

      let merged: CccdScanResult = blank;
      for (const { b64, detect } of detections) {
        if (detect) {
          merged = mergeDocResult(merged, detect, b64);
        } else {
          merged = {
            ...merged,
            extraImages: [...merged.extraImages, { base64: b64, docType: 'other' as DocType }],
          };
        }
      }

      if (prevPortrait && !merged.portraitBase64) {
        merged = { ...merged, portraitBase64: prevPortrait, faceMatchScore: null };
      }

      setScanMethod('manual');
      setResult(merged);

      if (merged.avatarBase64) {
        toast.success('Đã nhận dạng ảnh chân dung.');
      } else {
        toast('Không phát hiện CCCD — hãy nhập thủ công hoặc bổ sung ảnh CCCD.');
      }
    }

    setDetectingExtras(false);
  };

  const handleSingleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      await processUploadedFiles(files);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const { files } = e.dataTransfer;
    if (!files.length) return;
    setUploading(true);
    try {
      await processUploadedFiles(files);
    } finally {
      setUploading(false);
    }
  };

  const clearBatch = () => {
    setBatchItems([]);
    setBatchIndex(0);
    setBatchDone(0);
    localStorage.removeItem(STORAGE_KEY_BATCH);
    localStorage.removeItem(STORAGE_KEY_BATCH_IDX);
  };

  const cancelLatestBatch = () => {
    if (latestBatchId === null) {
      clearBatch();
      return;
    }
    setBatchItems(prev => {
      const next = prev.filter(b => b.batchId !== latestBatchId);
      try { localStorage.setItem(STORAGE_KEY_BATCH, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    setBatchIndex(0);
    setLatestBatchId(null);
    setShowCancelConfirm(false);
  };

  const skipCurrent = () => {
    if (!isBatchMode) return;
    setBatchItems(prev => prev.filter((_, i) => i !== batchIndex));
    setBatchIndex(prev => Math.min(prev, Math.max(0, batchItems.length - 2)));
  };

  const switchToManual = () => {
    setInputMode('manual');
    clearBatch();
    setScanMethod('manual');
    setResult({
      soCCCD: '', soCMT: '', hoTen: '', ngaySinh: '', gioiTinh: '', diaChi: '', diaChiMoi: '', ngayCap: '',
      avatarBase64: null, sourceImageBase64: null, detectedMethod: null, extraImages: [],
      soGPLX: '', hangGPLX: '', hangGPLXMoi: '', ngayCapGPLX: '', noiCapGPLX: '', donViCapGPLX: '', ngayTrungTuyenGPLX: '', ngayHetHanGPLX: '', maDvhc: '',
    });
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (avatarFileRef.current) avatarFileRef.current.value = '';

    const rawBase64 = await fileToBase64(file);

    // Try to crop face via Python service (YuNet + blue background)
    let croppedBase64: string | null = null;
    try {
      const fd = new FormData();
      fd.append('image', file, 'avatar.jpg');
      const resp = await fetch('/api/file/qr/crop-face', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
        body: fd,
      });
      const json = await resp.json();
      if (json.EC === 0 && json.DT) {
        croppedBase64 = `data:image/jpeg;base64,${json.DT}`;
        toast.success('Đã cắt khuôn mặt từ ảnh');
      }
    } catch { /* fallback to raw */ }

    const finalBase64 = croppedBase64 || rawBase64;
    if (!croppedBase64) {
      toast.info('Không phát hiện khuôn mặt — sử dụng ảnh gốc');
    }

    if (isBatchMode && currentBatch?.result) {
      setBatchItems(prev => prev.map((item, idx) =>
        idx === batchIndex ? { ...item, result: { ...item.result!, avatarBase64: finalBase64, faceMatchScore: null } } : item
      ));
    } else if (result) {
      setResult({ ...result, avatarBase64: finalBase64, faceMatchScore: null });
    } else {
      const blank: CccdScanResult = {
        soCCCD: '', soCMT: '', hoTen: '', ngaySinh: '', gioiTinh: '', diaChi: '', diaChiMoi: '', ngayCap: '',
        avatarBase64: finalBase64, sourceImageBase64: null, detectedMethod: null, extraImages: [],
        soGPLX: '', hangGPLX: '', hangGPLXMoi: '', ngayCapGPLX: '', noiCapGPLX: '', donViCapGPLX: '', ngayTrungTuyenGPLX: '', ngayHetHanGPLX: '', maDvhc: '',
      };
      setResult(blank);
      setScanMethod('manual');
    }
  };

  const handleAddExtraImages = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setDetectingExtras(true);

    // Detect all new images in parallel
    const detectTasks = Array.from(files).map(async (file) => {
      const [b64, detectResp] = await Promise.all([
        fileToBase64(file),
        detectDocumentApi(file).catch(() => null),
      ]);
      return { b64, detect: detectResp?.EC === 0 ? detectResp.DT : null };
    });

    const results = await Promise.all(detectTasks);

    if (isBatchMode && currentBatch?.result) {
      setBatchItems(prev => prev.map((item, idx) => {
        if (idx !== batchIndex || !item.result) return item;
        let merged = { ...item.result };
        for (const { b64, detect } of results) {
          if (detect) {
            merged = mergeDocResult(merged, detect, b64);
          } else {
            merged = { ...merged, extraImages: [...merged.extraImages, { base64: b64, docType: 'other' as DocType }] };
          }
        }
        return { ...item, result: merged };
      }));
    } else if (result) {
      let merged = { ...result };
      for (const { b64, detect } of results) {
        if (detect) {
          merged = mergeDocResult(merged, detect, b64);
        } else {
          merged = { ...merged, extraImages: [...merged.extraImages, { base64: b64, docType: 'other' as DocType }] };
        }
      }
      setResult(merged);
    }

    setDetectingExtras(false);
    if (extraFileRef.current) extraFileRef.current.value = '';
  };

  const removeExtraImage = (imgIndex: number) => {
    // Confirm before deletion — operator reported accidentally tapping
    // the × on a supplementary image and losing it irretrievably. The
    // confirm adds one tap but prevents the "oops, had to scan again"
    // workflow failure.
    // eslint-disable-next-line no-alert
    const ok = window.confirm('Bạn có thực sự muốn xóa ảnh này khỏi hồ sơ?');
    if (!ok) return;
    let nextExtras: ExtraImage[] = [];
    if (isBatchMode && currentBatch?.result) {
      nextExtras = currentBatch.result.extraImages.filter((_, i) => i !== imgIndex);
      setBatchItems(prev => prev.map((item, idx) =>
        idx === batchIndex
          ? { ...item, result: { ...item.result!, extraImages: nextExtras } }
          : item
      ));
    } else if (result) {
      nextExtras = result.extraImages.filter((_, i) => i !== imgIndex);
      setResult({ ...result, extraImages: nextExtras });
    }
    // Persist the deletion immediately when in review mode so an F5
    // doesn't bring the image back.
    if (isReviewMount) {
      scheduleAutoSave({ extraImagesJson: JSON.stringify(nextExtras) });
    }
  };

  const hasData = displayResult && displayResult.soCCCD && displayResult.soCCCD !== 'null';
  const isManual = inputMode === 'manual';

  // Simplified dashboard: just 2 big cards for adding records. The full
  // form layout is moved into RecordDetailModal (opened from "Chờ lưu hồ
  // sơ" tab). Keeps the landing view lightweight — operator only sees
  // "Thêm hồ sơ mới" + "Nhập theo lô CCCD".
  const openStaging = () => {
    // Always start a fresh record at the CCCD pill. Operators open this
    // overlay hundreds of times a day and the first doc is almost always
    // a CCCD — leaving the previous session's hint ("khác" / "gplx")
    // selected forced an extra tap every time.
    setActiveHint('cccd');
    setStagingOpen(true);
  };
  const closeStaging = () => {
    setStagingOpen(false);
    setStagingItems([]);
  };
  // Threshold for auto-advancing the capture hint pill after operator
  // finishes collecting a doc type. 2 CCCD (front+back), 1 portrait
  // (ảnh thẻ). GPLX is left as the final stop — operator decides when
  // they're done (GPLX often optional, 1 or 2 sides).
  const AUTO_ADVANCE: Partial<Record<DocTypeHint, { threshold: number; next: DocTypeHint }>> = {
    cccd:     { threshold: 2, next: 'portrait' },
    portrait: { threshold: 1, next: 'gplx' },
  };

  // Hard limits per hint. CCCD/GPLX cap at 2 (front + back); portrait is a
  // single-shot avatar; "other"/"mixed" are catch-alls and not limited.
  const HINT_MAX: Partial<Record<DocTypeHint, number>> = {
    cccd: 2,
    gplx: 2,
    portrait: 1,
  };

  const flashOverLimit = (hint: DocTypeHint) => {
    setFlashingHints(prev => {
      const next = new Set(prev);
      next.add(hint);
      return next;
    });
    setTimeout(() => {
      setFlashingHints(prev => {
        const next = new Set(prev);
        next.delete(hint);
        return next;
      });
    }, 1600);
  };

  const appendStaging = (files: FileList | File[], hint: DocTypeHint = activeHint) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    const max = HINT_MAX[hint];

    setStagingItems(prev => {
      const currentOfHint = prev.filter(it => it.hint === hint).length;
      const remaining = max == null ? arr.length : Math.max(0, max - currentOfHint);
      const accepted = arr.slice(0, remaining);
      const rejected = arr.length - accepted.length;

      if (rejected > 0) {
        flashOverLimit(hint);
        const label = hint === 'cccd' ? 'CCCD' : hint === 'gplx' ? 'GPLX' : hint === 'portrait' ? 'Ảnh thẻ' : hint;
        toast.warn(
          `${label} chỉ tối đa ${max} ảnh. Vui lòng xoá bớt ảnh đang có rồi thêm lại.`,
          { toastId: `staging-over-${hint}`, autoClose: 3500 },
        );
      }

      if (accepted.length === 0) return prev;

      // crypto.randomUUID is widely available; fall back to a counter-
      // based id for very old browsers (rare on operator devices).
      const newId = (): string => {
        try {
          if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
          }
        } catch { /* fall through */ }
        return `staging-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      };
      const combined = [...prev, ...accepted.map(file => ({ id: newId(), file, hint }))];
      const rule = AUTO_ADVANCE[hint];
      if (rule) {
        const after = combined.filter(it => it.hint === hint).length;
        const before = after - accepted.length;
        if (after >= rule.threshold && before < rule.threshold) {
          setActiveHint(rule.next);
        }
      }
      return combined;
    });
    setStagingOpen(true);
  };
  const removeStagingAt = (idx: number) => {
    setStagingItems(prev => prev.filter((_, i) => i !== idx));
  };
  const setStagingHintAt = (idx: number, hint: DocTypeHint) => {
    setStagingItems(prev => {
      const max = HINT_MAX[hint];
      if (max != null) {
        // Re-tagging would overshoot the per-hint cap → reject and flash red.
        const currentOfHint = prev.filter((it, i) => it.hint === hint && i !== idx).length;
        if (currentOfHint >= max) {
          flashOverLimit(hint);
          toast.warn(
            `Loại này đã đủ ${max} ảnh — đổi loại khác hoặc xoá bớt.`,
            { toastId: `staging-retag-${hint}`, autoClose: 3500 },
          );
          return prev;
        }
      }
      return prev.map((it, i) => (i === idx ? { ...it, hint } : it));
    });
  };
  // For nâng-hạng courses an upgrade record can't be filed without the
  // student's prior GPLX, so we gate the "Gửi" button on at least one
  // staged image being tagged as GPLX. Detected at render time so the
  // UI can also show a hint banner & disable the primary button.
  const stagedGplxCount = stagingItems.filter((it) => it.hint === 'gplx').length;
  const blockedByNangHang = isNangHangCourse && stagedGplxCount === 0 && stagingItems.length > 0;

  const sendStaging = () => {
    if (stagingItems.length === 0) return;
    if (blockedByNangHang) {
      flashOverLimit('gplx');
      toast.warn(
        'Khóa nâng hạng — cần thêm ít nhất 1 ảnh GPLX trước khi gửi.',
        { toastId: 'staging-nang-hang-need-gplx', autoClose: 4000 },
      );
      return;
    }
    // Prefer the hinted callback (faster detection); fall back to plain
    // onAddToQueue so this component stays compatible with older host pages.
    if (onAddToQueueWithHints) {
      onAddToQueueWithHints(stagingItems);
    } else if (onAddToQueue) {
      onAddToQueue(stagingItems.map(it => it.file));
    } else {
      return;
    }
    setStagingItems([]);
    setStagingOpen(false);
  };

  if (simpleMode) {
    return (
      <div className="tt-scanner-simple">
        <button
          type="button"
          className="tt-scanner-card"
          onClick={openStaging}
          disabled={uploading}
          onDrop={(e) => {
            e.preventDefault();
            if (e.dataTransfer.files.length) appendStaging(e.dataTransfer.files);
          }}
          onDragOver={(e) => e.preventDefault()}
        >
          <i className="material-icons tt-scanner-card__icon">person_add</i>
          <span className="tt-scanner-card__title">Thêm hồ sơ mới</span>
          <span className="tt-scanner-card__hint">
            Chụp hoặc chọn nhiều ảnh cho 1 học viên<br />
            (CCCD trước/sau, GPLX, ảnh thẻ, sức khỏe...)
          </span>
          {queueCount > 0 && (
            <div className="tt-scanner-card__queue-hint">
              {queuePendingCount > 0 && (
                <span><span className="tt-spinner tt-spinner--sm" /> {queuePendingCount} đang xử lý</span>
              )}
              {queueDoneCount > 0 && (
                <span className="tt-scanner-card__queue-hint--done">✓ {queueDoneCount} sẵn sàng</span>
              )}
            </div>
          )}
        </button>

        {/* Batch-by-CCCD card — needs a parent-supplied handler that
            wires into a course-scoped pending queue. Teachers don't
            have one (no course context, no backend pending row owned
            by them), so the card is hidden when the prop is absent.
            NVTT always passes onAddBatchFiles → still gets the card. */}
        {onAddBatchFiles && (
          <button
            type="button"
            className="tt-scanner-card tt-scanner-card--batch"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            onDrop={(e) => {
              e.preventDefault();
              if (e.dataTransfer.files.length && onAddBatchFiles) {
                onAddBatchFiles(Array.from(e.dataTransfer.files));
              }
            }}
            onDragOver={(e) => e.preventDefault()}
          >
            <i className="material-icons tt-scanner-card__icon">cloud_upload</i>
            <span className="tt-scanner-card__title">Nhập theo lô CCCD</span>
            <span className="tt-scanner-card__hint">
              Kéo thả hoặc chọn nhiều ảnh CCCD<br />
              (mỗi ảnh = 1 học viên khác nhau)
            </span>
          </button>
        )}

        {/* Hidden inputs — one per card to keep targeting simple */}
        <input
          ref={queueFileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => {
            if (e.target.files && onAddToQueue) onAddToQueue(e.target.files);
            if (queueFileRef.current) queueFileRef.current.value = '';
          }}
          className="tt-scanner__file-hidden"
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => {
            if (e.target.files && onAddBatchFiles) {
              onAddBatchFiles(Array.from(e.target.files));
            }
            if (fileRef.current) fileRef.current.value = '';
          }}
          className="tt-scanner__file-hidden"
        />
        {/* Camera (mobile) — single-shot; appends each photo to staging */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              appendStaging(e.target.files);
            }
            if (cameraInputRef.current) cameraInputRef.current.value = '';
          }}
          className="tt-scanner__file-hidden"
        />
        {/* Gallery — multi-select to staging (mobile & desktop) */}
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              appendStaging(e.target.files);
            }
            if (galleryInputRef.current) galleryInputRef.current.value = '';
          }}
          className="tt-scanner__file-hidden"
        />

        {stagingOpen && (() => {
          const HINTS: { key: DocTypeHint; label: string; icon: string }[] = [
            { key: 'cccd',     label: 'CCCD',     icon: 'badge' },
            { key: 'gplx',     label: 'GPLX',     icon: 'directions_car' },
            { key: 'portrait', label: 'Ảnh thẻ',  icon: 'face' },
            { key: 'other',    label: 'Khác',     icon: 'description' },
          ];
          const hintLabelOf = (h: DocTypeHint) => HINTS.find(x => x.key === h)?.label || h;
          return (
          <div
            className="tt-staging"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget) closeStaging(); }}
          >
            <div className="tt-staging__sheet">
              <div className="tt-staging__header">
                <span className="tt-staging__title">
                  Ảnh cho 1 học viên
                  {stagingItems.length > 0 && (
                    <span className="tt-staging__count">{stagingItems.length}</span>
                  )}
                </span>
                <button
                  type="button"
                  className="tt-staging__close"
                  onClick={closeStaging}
                  aria-label="Đóng"
                >
                  <i className="material-icons">close</i>
                </button>
              </div>

              {/* Type pill row — picks the tag applied to the NEXT photo(s)
                  captured or picked. Operator can also change the hint per
                  thumbnail after the fact. */}
              <div className="tt-staging__hints" role="tablist" aria-label="Loại giấy tờ">
                {HINTS.map(h => (
                  <button
                    key={h.key}
                    type="button"
                    role="tab"
                    aria-selected={activeHint === h.key}
                    className={`tt-staging__hint tt-staging__hint--${h.key} ${activeHint === h.key ? 'tt-staging__hint--active' : ''}`}
                    onClick={() => setActiveHint(h.key)}
                  >
                    <i className="material-icons">{h.icon}</i>
                    {h.label}
                  </button>
                ))}
              </div>

              {stagingItems.length === 0 ? (
                <div className="tt-staging__empty">
                  <i className="material-icons">photo_camera</i>
                  <p>Chưa có ảnh nào. Chọn loại rồi chụp/tải ảnh.</p>
                </div>
              ) : (
                <div className="tt-staging__thumbs">
                  {stagingItems.map((item, i) => (
                    <div
                      key={item.id}
                      className={`tt-staging__thumb tt-staging__thumb--${item.hint}${flashingHints.has(item.hint) ? ' tt-staging__thumb--flash-warn' : ''}`}
                    >
                      <img src={stagingThumbsRef.current.get(item.id) || ''} alt={`Ảnh ${i + 1}`} />
                      <button
                        type="button"
                        className="tt-staging__thumb-remove"
                        onClick={() => removeStagingAt(i)}
                        aria-label="Xoá ảnh"
                      >
                        <i className="material-icons">close</i>
                      </button>
                      <span className="tt-staging__thumb-idx">{i + 1}</span>
                      {/* Per-thumb hint selector — long-press alternative. */}
                      <select
                        className="tt-staging__thumb-hint"
                        value={item.hint}
                        onChange={(e) => setStagingHintAt(i, e.target.value as DocTypeHint)}
                        aria-label="Đổi loại giấy tờ"
                      >
                        {HINTS.map(h => (
                          <option key={h.key} value={h.key}>{h.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}

              <div className="tt-staging__actions">
                <button
                  type="button"
                  className="tt-staging__btn tt-staging__btn--ghost"
                  onClick={() => cameraInputRef.current?.click()}
                >
                  <i className="material-icons">photo_camera</i>
                  Chụp {hintLabelOf(activeHint)}
                </button>
                <button
                  type="button"
                  className="tt-staging__btn tt-staging__btn--ghost"
                  onClick={() => galleryInputRef.current?.click()}
                >
                  <i className="material-icons">photo_library</i>
                  Thư viện
                </button>
                <button
                  type="button"
                  className="tt-staging__btn tt-staging__btn--primary"
                  onClick={sendStaging}
                  disabled={stagingItems.length === 0 || blockedByNangHang}
                  title={blockedByNangHang ? 'Cần thêm ảnh GPLX (khóa nâng hạng)' : undefined}
                >
                  <i className="material-icons">send</i>
                  Gửi {stagingItems.length > 0 ? `${stagingItems.length} ảnh` : ''}
                </button>
              </div>
              {blockedByNangHang && (
                <div className="tt-staging__warn-banner">
                  <i className="material-icons">badge</i>
                  Khóa <b>nâng hạng</b> bắt buộc có ảnh GPLX. Hãy thêm ít nhất 1 ảnh và đổi loại sang <b>GPLX</b> trước khi gửi.
                </div>
              )}
            </div>
          </div>
          );
        })()}
      </div>
    );
  }

  return (
    <div className="tt-scanner">
      {/* Header with tabs — hidden in review mount because the parent
          RecordDetailModal renders its own header bar with course info +
          close. Showing both stacked them and "Nhập hồ sơ CCCD" + the
          Tải ảnh/Thủ công tabs are meaningless during review anyway. */}
      {!isReviewMount && (
        <div className="tt-scanner__header">
          <div className="tt-scanner__header-left">
            <i className="material-icons">badge</i>
            <div>
              <div className="tt-scanner__header-title">Nhập hồ sơ CCCD</div>
              <div className="tt-scanner__header-sub">Tải ảnh (kéo thả nhiều) hoặc nhập thủ công</div>
            </div>
          </div>
          <div className="tt-scanner__tabs">
            <button
              type="button"
              className={`tt-scanner__tab ${inputMode === 'upload' ? 'tt-scanner__tab--active' : ''}`}
              onClick={() => { setInputMode('upload'); }}
            >
              <i className="material-icons">upload_file</i>
              Tải ảnh
            </button>
            <button
              type="button"
              className={`tt-scanner__tab ${inputMode === 'manual' ? 'tt-scanner__tab--active' : ''}`}
              onClick={switchToManual}
            >
              <i className="material-icons">edit</i>
              Thủ công
            </button>
          </div>
        </div>
      )}

      {/* Body — upload dropzone LEFT + form RIGHT.
          When non-batch and a scan is in progress (phase !== 'idle'), the
          left column is hidden so the user cannot drop another file while
          the current one is being processed — the form col expands to full
          width and the scan progress/result takes the focus. */}
      <div className={`tt-scanner__body${!isBatchMode && isScanBusy ? ' tt-scanner__body--busy' : ''}`}>
        {/* Left column — dropzone when idle, image viewer + doc tabs when scan is done */}
        {(isBatchMode || !isScanBusy) && (
        <div className="tt-scanner__camera-col">
          {inputMode === 'upload' && (
            isBatchMode ? (
              <div className="tt-scanner__source-preview">
                {/* Review-mount — only compact thumbnails of EXTRA images
                    (the additional CCCD back / GPLX pages). The primary
                    CCCD preview is omitted: operators already OCR'd it
                    and don't need to re-check it from the review modal.
                    Portrait avatar is rendered separately in the right
                    column, not in this strip. */}
                {isReviewMount ? (
                  <>
                    {(currentBatch?.result?.extraImages?.length ?? 0) > 0 ? (
                    <div className="tt-scanner__review-gallery">
                      <div className="tt-scanner__review-gallery-header">
                        <i className="material-icons">collections</i>
                        <span>Ảnh bổ sung</span>
                      </div>
                      <div className="tt-scanner__review-gallery-grid">
                        {(currentBatch?.result?.extraImages || []).map((img, i) => (
                          <div key={i} style={{ position: 'relative' }}>
                            <button
                              type="button"
                              className="tt-scanner__review-gallery-thumb"
                              onClick={() => setZoomImageUrl(img.base64)}
                              title="Phóng to"
                              style={{ width: '100%' }}
                            >
                              <img src={img.base64} alt={img.label || img.docType} />
                              <span className="tt-scanner__review-gallery-label">
                                {img.docType === 'cccd' ? 'CCCD' : img.docType === 'gplx' ? 'GPLX' : img.docType === 'health' ? 'Sức khoẻ' : 'Khác'}
                              </span>
                            </button>
                            <button
                              type="button"
                              className="tt-scanner__review-gallery-delete"
                              onClick={(e) => { e.stopPropagation(); removeExtraImage(i); }}
                              title="Xóa ảnh này"
                              aria-label="Xóa ảnh"
                            >
                              <i className="material-icons">close</i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    ) : null}
                  </>
                ) : (
                  currentBatch?.preview && (
                    <ZoomableImage src={currentBatch.preview} alt="CCCD" className="tt-scanner__source-img" />
                  )
                )}
                {/* Batch nav bar */}
                <div className="tt-scanner__batch-nav">
                  <button
                    type="button"
                    className="tt-scanner__batch-nav-btn"
                    disabled={batchIndex <= 0}
                    onClick={() => setBatchIndex(prev => prev - 1)}
                  >
                    <i className="material-icons">chevron_left</i>
                  </button>
                  <span className="tt-scanner__batch-nav-label">
                    {batchIndex + 1} / {batchItems.length}
                  </span>
                  <button
                    type="button"
                    className="tt-scanner__batch-nav-btn"
                    disabled={batchIndex >= batchItems.length - 1}
                    onClick={() => setBatchIndex(prev => prev + 1)}
                  >
                    <i className="material-icons">chevron_right</i>
                  </button>
                </div>
                <button
                  type="button"
                  className="tt-scanner__source-change"
                  onClick={() => setShowCancelConfirm(true)}
                >
                  <i className="material-icons">close</i>
                  Hủy lô
                </button>
                {showCancelConfirm && (
                  <div className="tt-scanner__cancel-confirm">
                    <p>Hủy lô mới nhất? Các hồ sơ trước đó chưa xử lý sẽ được giữ lại.</p>
                    <div className="tt-scanner__cancel-confirm-actions">
                      <button
                        type="button"
                        className="tt-btn tt-btn--outline tt-btn--sm"
                        onClick={() => setShowCancelConfirm(false)}
                      >
                        Không
                      </button>
                      <button
                        type="button"
                        className="tt-btn tt-btn--danger tt-btn--sm"
                        onClick={cancelLatestBatch}
                      >
                        Hủy lô
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (displaySource || gplxImage) && !isBatchMode ? (
              <div className="tt-scanner__doc-viewer">
                {/* Doc tabs — only when GPLX is detected too (else single-doc view) */}
                {hasGplxData && (
                  <div className="tt-scanner__doc-tabs">
                    <button
                      type="button"
                      className={`tt-scanner__doc-tab${activeDocTab === 'cccd' ? ' tt-scanner__doc-tab--active' : ''}`}
                      onClick={() => setActiveDocTab('cccd')}
                    >
                      <i className="material-icons">badge</i>
                      CCCD
                    </button>
                    <button
                      type="button"
                      className={`tt-scanner__doc-tab${activeDocTab === 'gplx' ? ' tt-scanner__doc-tab--active' : ''}`}
                      onClick={() => setActiveDocTab('gplx')}
                    >
                      <i className="material-icons">directions_car</i>
                      GPLX
                    </button>
                    <button
                      type="button"
                      className={`tt-scanner__doc-tab${activeDocTab === 'all' ? ' tt-scanner__doc-tab--active' : ''}`}
                      onClick={() => setActiveDocTab('all')}
                    >
                      <i className="material-icons">dashboard</i>
                      Tổng quan
                    </button>
                  </div>
                )}

                {/* Image viewer per active tab — each image has its own 3:4
                    frame so stacking (Tổng quan mode) doesn't squash them */}
                {(activeDocTab === 'cccd' || activeDocTab === 'all' || !hasGplxData) && displaySource && (
                  <div className="tt-scanner__doc-image">
                    {activeDocTab === 'all' && <span className="tt-scanner__doc-image-label">Ảnh CCCD</span>}
                    <div className="tt-scanner__source-preview">
                      <ZoomableImage src={displaySource} alt="CCCD" className="tt-scanner__source-img" />
                    </div>
                  </div>
                )}
                {(activeDocTab === 'gplx' || activeDocTab === 'all') && gplxImage && (
                  <div className="tt-scanner__doc-image">
                    {activeDocTab === 'all' && <span className="tt-scanner__doc-image-label">Ảnh GPLX</span>}
                    <div className="tt-scanner__source-preview">
                      <ZoomableImage src={gplxImage} alt="GPLX" className="tt-scanner__source-img" />
                    </div>
                  </div>
                )}
                {activeDocTab === 'gplx' && !gplxImage && (
                  <div className="tt-scanner__doc-image tt-scanner__doc-image--empty">
                    <i className="material-icons">directions_car</i>
                    <span>Chưa có ảnh GPLX — hãy tải lên ở "Thêm ảnh" bên dưới.</span>
                  </div>
                )}

                {/* "Chọn ảnh khác" — placed OUTSIDE the image frame so it no
                    longer overlaps CCCD content at the bottom of the card */}
                <button
                  type="button"
                  className="tt-btn tt-btn--outline tt-btn--sm tt-scanner__doc-change"
                  onClick={() => { reset(); if (fileRef.current) fileRef.current.click(); }}
                >
                  <i className="material-icons">refresh</i>
                  Chọn ảnh khác
                </button>
              </div>
            ) : onAddToQueue ? (
              <div className="tt-scanner__queue-mode">
                {/* Queue add card — big, always visible so operator can keep
                    adding records continuously without the UI ever "locking
                    up" with a single-record preview. */}
                <button
                  type="button"
                  className="tt-scanner__add-card"
                  onClick={() => queueFileRef.current?.click()}
                  disabled={uploading}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer.files.length) {
                      onAddToQueue(e.dataTransfer.files);
                    }
                  }}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <i className="material-icons tt-scanner__add-card-icon">person_add</i>
                  <span className="tt-scanner__add-card-title">Thêm hồ sơ mới</span>
                  <span className="tt-scanner__add-card-hint">
                    Chọn ảnh cho 1 học viên (CCCD, GPLX, ảnh thẻ, sức khỏe...)
                  </span>
                  <span className="tt-scanner__add-card-hint">
                    Hệ thống xử lý ngầm ở tab "Chờ lưu hồ sơ"
                  </span>
                </button>

                {queueCount > 0 && (
                  <div className="tt-queue-hint">
                    {queuePendingCount > 0 && (
                      <span className="tt-queue-hint__badge tt-queue-hint__badge--processing">
                        <span className="tt-spinner tt-spinner--sm" />
                        {queuePendingCount} đang xử lý
                      </span>
                    )}
                    {queueDoneCount > 0 && (
                      <span className="tt-queue-hint__badge tt-queue-hint__badge--done">
                        <i className="material-icons">check_circle</i>
                        {queueDoneCount} sẵn sàng
                      </span>
                    )}
                    <span className="tt-queue-hint__text">
                      → Xem chi tiết ở tab "Chờ lưu hồ sơ"
                    </span>
                  </div>
                )}

                <input
                  ref={queueFileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    if (e.target.files && onAddToQueue) {
                      onAddToQueue(e.target.files);
                    }
                    if (queueFileRef.current) queueFileRef.current.value = '';
                  }}
                  className="tt-scanner__file-hidden"
                />
              </div>
            ) : (
              <label
                className="tt-scanner__dropzone"
                htmlFor="tt-scanner-file"
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                <i className="material-icons">cloud_upload</i>
                <span className="tt-scanner__dropzone-text">Chọn hoặc kéo thả ảnh học viên</span>
                <span className="tt-scanner__dropzone-hint">JPG, PNG — nhiều ảnh (CCCD, GPLX, sức khỏe...)</span>
                <input
                  id="tt-scanner-file"
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleSingleUpload}
                  className="tt-scanner__file-hidden"
                />
              </label>
            )
          )}

          {inputMode === 'manual' && (
            <div className="tt-scanner__viewport" style={{ background: '#f1f3f4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="tt-scanner__placeholder" style={{ color: '#80868b' }}>
                <i className="material-icons">edit_note</i>
                <span>Nhập thủ công</span>
              </div>
            </div>
          )}

          {/* Extra images (GPLX, health cert, etc.) — ONLY shown when NOT
              in review mount. Review mount has its own compact gallery
              in the left column already ("Ảnh bổ sung" via
              tt-scanner__review-gallery); rendering this section too
              showed two identical strips in the detail popup. */}
          {(displayResult || isBatchMode) && !isReviewMount && (
            <div className="tt-scanner__extras">
              <div className="tt-scanner__extras-header">
                <span className="tt-scanner__extras-title">
                  <i className="material-icons">collections</i>
                  Ảnh bổ sung
                </span>
                <button
                  type="button"
                  className="tt-btn tt-btn--outline tt-btn--sm"
                  onClick={() => extraFileRef.current?.click()}
                >
                  <i className="material-icons">add_photo_alternate</i>
                  Thêm ảnh
                </button>
                <input
                  ref={extraFileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleAddExtraImages}
                  className="tt-scanner__file-hidden"
                />
              </div>
              {(displayResult?.extraImages?.length ?? 0) > 0 ? (
                <div className="tt-scanner__extras-grid">
                  {displayResult!.extraImages.map((img, i) => (
                    <div key={i} className="tt-scanner__extras-card">
                      <img
                        src={img.base64}
                        alt={img.label || img.docType}
                        className="tt-scanner__extras-thumb"
                        onClick={() => setZoomImageUrl(img.base64)}
                        style={{ cursor: 'zoom-in' }}
                      />
                      <span className={`tt-scanner__extras-badge tt-scanner__extras-badge--${img.docType}`}>
                        {img.docType === 'cccd' ? 'CCCD' : img.docType === 'gplx' ? 'GPLX' : img.docType === 'health' ? 'Sức khỏe' : 'Khác'}
                      </span>
                      <button
                        type="button"
                        className="tt-scanner__extras-remove"
                        onClick={() => removeExtraImage(i)}
                        title="Xóa"
                      >
                        <i className="material-icons">close</i>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="tt-scanner__extras-empty">GPLX, giấy khám sức khỏe, giấy tờ khác...</p>
              )}
            </div>
          )}
        </div>
        )}

        {/* Right column — result fields */}
        <div className="tt-scanner__form-col">
          {/* Giáo viên phụ trách — chỉ hiển thị ở review mount (popup
              chi tiết "Chờ lưu hồ sơ"). Field nằm trên cùng của cột
              form-col để operator chọn giáo viên ngay khi mở popup, và
              để dropdown có đủ chiều rộng + không bị clip.
              Auto-save chip cũng render ngay cạnh teacher label —
              trước nó nằm ở camera-col cạnh "Ảnh bổ sung" gây rối
              mắt; đưa về đầu khu thông tin để operator thấy đồng thời
              trạng thái lưu + các field họ đang sửa. */}
          {isReviewMount && !teacherFieldHidden && (
            <div
              className={`tt-scanner__teacher-field${(!currentBatch?.result?.teacherId || fieldErrors.teacherId) ? ' tt-scanner__teacher-field--error' : ''}`}
              data-error-field="teacherId"
            >
              <label className="tt-scanner__teacher-field-label">
                <i className="material-icons">school</i>
                Giáo viên phụ trách <span style={{ color: '#dc2626' }}>*</span>
                {autoSaveStatus !== 'idle' && (
                  <span className={`tt-scanner__autosave-chip tt-scanner__autosave-chip--${autoSaveStatus}`}>
                    {autoSaveStatus === 'saving' && <><span className="tt-spinner tt-spinner--sm" /> Đang lưu…</>}
                    {autoSaveStatus === 'saved' && <><i className="material-icons">check_circle</i> Đã lưu</>}
                    {autoSaveStatus === 'error' && <><i className="material-icons">error</i> Lỗi lưu</>}
                  </span>
                )}
                <span className="tt-scanner__teacher-field-hint">
                  supper teacher dạy học viên này
                </span>
              </label>
              <TeacherAutocomplete
                value={currentBatch?.result?.teacherId ?? null}
                initialDisplayName={currentBatch?.result?.teacherFullName || undefined}
                onChange={(teacherId, fullName) => {
                  if (isBatchMode && currentBatch?.result) {
                    setBatchItems(prev => prev.map((item, idx) =>
                      idx === batchIndex && item.result
                        ? { ...item, result: { ...item.result, teacherId, teacherFullName: fullName } }
                        : item,
                    ));
                  }
                  scheduleAutoSave({ teacherId });
                  if (teacherId) clearFieldError('teacherId');
                }}
              />
              {fieldErrors.teacherId && (
                <span className="tt-scanner__teacher-field-error">{fieldErrors.teacherId}</span>
              )}
            </div>
          )}

          {/* Compact source preview shown while processing (left col is hidden) */}
          {!isBatchMode && phase !== 'idle' && displaySource && (
            <div className="tt-scanner__source-strip">
              <button
                type="button"
                className="tt-scanner__source-thumb"
                onClick={() => setZoomImageUrl(displaySource)}
                title="Bấm để phóng to"
              >
                <img src={displaySource} alt="CCCD" />
                <span className="tt-scanner__source-thumb-hint">
                  <i className="material-icons">zoom_in</i>
                </span>
              </button>
              <div className="tt-scanner__source-strip-info">
                <span className="tt-scanner__source-strip-label">Đang xử lý hồ sơ hiện tại</span>
                <span className="tt-scanner__source-strip-sub">
                  {phase === 'scanning' || phase === 'qr-found' || phase === 'ocr-running'
                    ? 'Không thể thêm hồ sơ mới cho tới khi hoàn tất.'
                    : 'Xem lại thông tin rồi bấm Nộp hồ sơ hoặc Bỏ qua để quay lại.'}
                </span>
              </div>
            </div>
          )}

          {/* Batch progress bar */}
          {isBatchMode && batchProcessing && (
            <div className="tt-scanner__batch-progress">
              <span className="tt-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
              <span>Đang nhận dạng {batchDone}/{batchItems.length}...</span>
              <div className="tt-scanner__batch-progress-bar">
                <div
                  className="tt-scanner__batch-progress-fill"
                  style={{ width: `${(batchDone / batchItems.length) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Batch summary */}
          {isBatchMode && !batchProcessing && (
            <div className="tt-scanner__batch-summary">
              <span className="tt-scanner__batch-summary-ok">
                <i className="material-icons">check_circle</i> {batchSuccessCount} thành công
              </span>
              {batchErrorCount > 0 && (
                <span className="tt-scanner__batch-summary-err">
                  <i className="material-icons">error</i> {batchErrorCount} lỗi
                </span>
              )}
              <span className="tt-scanner__batch-summary-total">
                Còn {batchItems.length} hồ sơ
              </span>
            </div>
          )}

          {/* Inline action buttons — one record at a time, no picker.
              Records are processed top-down: save or skip dismisses the
              current item and the next one auto-loads. Clicking any
              thumbnail-style picker would allow mixing images between
              records, which the operator reported as confusing. */}
          {isBatchMode && batchItems.length > 0 && (
            <div className="tt-scanner__batch-bar tt-scanner__batch-bar--compact">
              <div className="tt-scanner__batch-actions">
                <span className="tt-scanner__batch-position">
                  <i className="material-icons">fact_check</i>
                  Hồ sơ {Math.min(batchIndex + 1, batchItems.length)} / {batchItems.length}
                </span>
                <button
                  type="button"
                  className="tt-btn tt-btn--outline tt-btn--sm"
                  onClick={skipCurrent}
                  disabled={batchProcessing || batchItems.length === 0 || readOnlyMode}
                  style={readOnlyMode ? { display: 'none' } : undefined}
                >
                  <i className="material-icons">skip_next</i>
                  Bỏ qua
                </button>
                {readOnlyMode ? (
                  <span
                    className="tt-btn tt-btn--outline tt-btn--sm"
                    style={{ background: '#f1f3f4', color: '#5f6368', cursor: 'default' }}
                    title="Hồ sơ đã được trung tâm xử lý — không thể chỉnh sửa"
                  >
                    <i className="material-icons">lock</i>
                    Chỉ xem
                  </span>
                ) : (
                  <button
                    type="button"
                    className="tt-btn tt-btn--success tt-btn--sm"
                    onClick={handleSubmit}
                    disabled={!currentBatch?.result?.soCCCD || !currentBatch?.result?.hoTen || batchProcessing}
                  >
                    <i className="material-icons">{submitIcon}</i>
                    {submitLabel || 'Lưu hồ sơ này'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Avatar + name row — CCCD avatar is read-only reference.
              When face extraction fails, the user can manually upload
              a crop of the CCCD face via the hidden file input. */}
          <div className="tt-scanner__avatar-row">
            <div className="tt-scanner__avatar-col">
              <div
                className="tt-scanner__avatar-wrap"
                onClick={() => {
                  if (displayResult?.avatarBase64) {
                    setAvatarZoom(true);
                  } else if (displayResult) {
                    avatarFileRef.current?.click();
                  }
                }}
                title={displayResult?.avatarBase64 ? 'Ảnh trên CCCD — bấm để phóng to' : 'Bấm để tải ảnh CCCD thủ công'}
                style={{ cursor: displayResult ? 'pointer' : 'default' }}
              >
                {displayResult?.avatarBase64 ? (
                  <img src={displayResult.avatarBase64} alt="Ảnh CCCD" className="tt-scanner__avatar" />
                ) : (
                  <div className="tt-scanner__avatar-placeholder tt-scanner__avatar-placeholder--clickable">
                    <i className="material-icons">add_a_photo</i>
                    <span>Tải ảnh CCCD</span>
                  </div>
                )}
              </div>
              <input
                ref={avatarFileRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="tt-scanner__file-hidden"
              />
              <span className="tt-scanner__avatar-caption">
                <i className="material-icons">badge</i>
                Ảnh CCCD (đối chiếu)
                {displayResult?.avatarBase64 && (
                  <button
                    type="button"
                    className="tt-scanner__avatar-change-btn"
                    onClick={(e) => { e.stopPropagation(); avatarFileRef.current?.click(); }}
                    title="Thay ảnh CCCD"
                  >
                    <i className="material-icons">edit</i>
                  </button>
                )}
              </span>
            </div>
            <div className="tt-scanner__avatar-info">
              <h3>{displayResult?.hoTen || '—'}</h3>
              <span className="tt-scanner__avatar-cccd">{displayResult?.soCCCD || '—'}</span>
            </div>
          </div>

          {/* Portrait uploader — the student profile photo that will
              become the displayed avatar after save. Every upload runs a
              face-match against the CCCD avatar above.

              onChange routes back into whichever state owns the current
              record: `result` for single mode, `batchItems[batchIndex]`
              for batch mode. Uses functional setters so the closure
              doesn't capture a stale `result` reference. */}
          <PortraitUploader
            cccdAvatarBase64={displayResult?.avatarBase64 || null}
            portraitBase64={displayResult?.portraitBase64 ?? null}
            portraitOriginalBase64={displayResult?.portraitOriginalBase64 ?? null}
            portraitAiVariantsJson={displayResult?.portraitAiVariantsJson ?? null}
            faceMatchScore={displayResult?.faceMatchScore ?? null}
            onChange={(pb, score, pbOrig, variants) => {
              const patch = {
                portraitBase64: pb,
                portraitOriginalBase64: pbOrig ?? null,
                portraitAiVariantsJson: variants ?? null,
                faceMatchScore: score,
              };
              // Detect what kind of change this is so we can skip the
              // round-trip when the operator is only switching which
              // variant is the "active" avatar — that's a local visual
              // pick, committed only when they click Lưu hồ sơ.
              const prevResult = isBatchMode ? currentBatch?.result : result;
              const origChanged = (prevResult?.portraitOriginalBase64 ?? null) !== (pbOrig ?? null);
              const variantsChanged = (prevResult?.portraitAiVariantsJson ?? null) !== (variants ?? null);
              const persistableChange = origChanged || variantsChanged;

              if (isBatchMode) {
                setBatchItems(prev => prev.map((item, idx) =>
                  idx === batchIndex && item.result
                    ? { ...item, result: { ...item.result, ...patch } }
                    : item,
                ));
              } else {
                setResult(prev => (prev ? { ...prev, ...patch } : prev));
              }
              // Only auto-save when the operator uploaded a new original
              // OR generated/deleted an AI variant — those mutations must
              // survive a page refresh. Switching `portraitBase64` alone
              // (clicking which thumbnail is highlighted green) is a
              // pending pick; the final commit happens when the operator
              // clicks Lưu hồ sơ.
              if (persistableChange) {
                scheduleAutoSave(patch);
              }
            }}
            // Never block based on avatarBase64 — operator can upload a
            // portrait directly even when the CCCD face crop failed.
            disabled={false}
          />

          {displayResult?.avatarBase64 && (
            <div className="tt-scanner__idphoto-row">
              <button
                type="button"
                className="tt-btn tt-btn--primary tt-btn--sm"
                onClick={handleGenerateIdPhoto}
                disabled={generatingIdPhoto}
                title="Tạo ảnh thẻ 3x4 bằng AI (nền xanh, áo vest)"
              >
                {generatingIdPhoto ? (
                  <>
                    <span className="tt-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                    Đang tạo ảnh thẻ AI...
                  </>
                ) : (
                  <>
                    <i className="material-icons">auto_awesome</i>
                    Tạo ảnh thẻ AI (từ CCCD)
                  </>
                )}
              </button>
            </div>
          )}

          {/* Avatar zoom popup */}
          {avatarZoom && displayResult?.avatarBase64 && (
            <ZoomOverlay
              src={displayResult.avatarBase64}
              alt="Avatar"
              onClose={() => setAvatarZoom(false)}
            />
          )}

          {/* Detection status for extra images */}
          {detectingExtras && (
            <div className="tt-scanner__detect-status">
              <span className="tt-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
              <span>Đang nhận dạng giấy tờ bổ sung...</span>
            </div>
          )}

          {/* CCCD Fields grid — hidden when GPLX tab is focused */}
          {showCccdFields && (
          <div className="tt-scanner__fields">
            {FIELD_CONFIG.map(({ key, label, icon, full }) => (
              <div
                className={`tt-scanner__field${full ? ' tt-scanner__field--full' : ''}`}
                key={key}
              >
                <i className="material-icons tt-scanner__field-icon">{icon}</i>
                <div className="tt-scanner__field-content">
                  <span className="tt-scanner__field-label">{label}</span>
                  {key === 'maDvhc' ? (
                    <>
                      <DvhcAutocomplete
                        value={displayResult?.maDvhc || ''}
                        displayText={dvhcDisplayText}
                        onChange={(maDvhc, tenDayDu) => {
                          setDvhcDisplayText(tenDayDu);
                          handleFieldChange('maDvhc', maDvhc);
                        }}
                      />
                      {!displayResult?.maDvhc && (
                        <span className="tt-scanner__field-hint">
                          {dvhcConverting
                            ? 'Đang chuyển đổi đơn vị hành chính...'
                            : dvhcConvertError
                              ? dvhcConvertError
                              : 'Sẽ chuyển đổi tự động khi có địa chỉ CCCD'}
                        </span>
                      )}
                    </>
                  ) : (
                    <input
                      type="text"
                      className="tt-scanner__field-input"
                      value={(displayResult as Record<string, unknown>)?.[key] as string || ''}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      placeholder={label}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
          )}

          {/* CCCD zoom tooltip removed — replaced by the in-field
              .tt-field-preview magnifier which shows the value itself in a
              large non-bold font so operators can verify diacritics per
              field. The previous image-based floating tooltip frequently
              failed to render (broken-image icon in the top-right corner)
              when sourceImageBase64 was stale / cross-origin. */}

          {/* GPLX Fields grid — visible on GPLX/Overview tab or manual/batch mode */}
          {(displayResult || isManual || isBatchMode) && (isManual || showGplxFields) && (
            <div className="tt-scanner__gplx-section">
              <div className="tt-scanner__gplx-header">
                <i className="material-icons">directions_car</i>
                <span>Giấy phép lái xe</span>
              </div>
              <div className="tt-scanner__fields">
                {GPLX_FIELD_CONFIG.map(({ key, label, icon, full, dropdown }) => (
                  <div
                    className={`tt-scanner__field${full ? ' tt-scanner__field--full' : ''}`}
                    key={key}
                  >
                    <i className="material-icons tt-scanner__field-icon">{icon}</i>
                    <div className="tt-scanner__field-content">
                      <span className="tt-scanner__field-label">{label}</span>
                      {dropdown ? (
                        <select
                          className="tt-scanner__field-input tt-scanner__field-select"
                          value={(displayResult as Record<string, unknown>)?.[key] as string || ''}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                        >
                          <option value="">— Chọn hạng mới —</option>
                          {NEW_HANG_OPTIONS.map(h => (
                            <option key={h.new} value={h.new}>{h.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="tt-scanner__field-input"
                          value={(displayResult as Record<string, unknown>)?.[key] as string || ''}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          placeholder={label}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons — only for non-batch mode (batch mode has them inline above) */}
          {!isBatchMode && (
            <div className="tt-scanner__form-actions">
              <button
                type="button"
                className="tt-btn tt-btn--outline"
                onClick={reset}
                disabled={!displayResult && phase === 'idle'}
              >
                <i className="material-icons">close</i>
                Bỏ qua
              </button>
              <button
                type="button"
                className="tt-btn tt-btn--success"
                onClick={handleSubmit}
                disabled={!displayResult?.soCCCD || !displayResult?.hoTen}
              >
                <i className="material-icons">{submitIcon}</i>
                {submitLabel}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Status bar */}
      {!isBatchMode && phase !== 'idle' && (
        <div className={`tt-scanner__status tt-scanner__status--${phase}`}>
          {(phase === 'scanning' || phase === 'ocr-running' || phase === 'qr-found') && (
            <span className="tt-spinner" />
          )}
          {phase === 'done' && <i className="material-icons">check_circle</i>}
          {phase === 'error' && <i className="material-icons">error_outline</i>}
          <span>{PHASE_LABELS[phase as ScanPhase]}</span>
        </div>
      )}

      {/* Extra image zoom modal — include info panel on the right so
          operator can verify the image content against the detected
          fields side-by-side. Gallery walks CCCD source + all extras so
          the operator can step through every image of this student
          without closing the overlay. */}
      {zoomImageUrl && (() => {
        const DOC_LABEL: Record<DocType, string> = {
          cccd: 'CCCD',
          gplx: 'GPLX',
          health: 'Giấy sức khoẻ',
          other: 'Giấy tờ khác',
        };
        // Build the gallery here — identity doesn't need to be stable
        // because ZoomOverlay now guards its [currentSrc] effect behind
        // `lastSrcRef` and its [src] effect behind `lastPropSrcRef`, so a
        // new gallery array each keystroke no longer causes the rotation
        // state or gallery index to churn.
        const gallery: { src: string; label?: string }[] = [];
        if (displaySource) gallery.push({ src: displaySource, label: 'CCCD chính' });
        (displayResult?.extraImages || []).forEach((img) => {
          // Skip duplicates (extras sometimes include the same base64 as source).
          if (gallery.some(g => g.src === img.base64)) return;
          gallery.push({
            src: img.base64,
            label: img.label || DOC_LABEL[img.docType] || 'Giấy tờ',
          });
        });
        // Ensure the clicked image appears in the gallery — otherwise the
        // overlay would default to index 0 when findIndex returns -1.
        if (!gallery.some(g => g.src === zoomImageUrl)) {
          gallery.unshift({ src: zoomImageUrl, label: 'Ảnh hiện tại' });
        }
        return (
        <ZoomOverlay
          src={zoomImageUrl}
          alt="Zoom"
          onClose={() => { setZoomImageUrl(null); setZoomActiveGroup(null); }}
          gallery={gallery}
          onActiveChange={(item) => {
            // Classify the currently-viewed image by label prefix. The
            // gallery labels come from the backend's docType mapping
            // ("CCCD chính", "GPLX", "Giấy sức khoẻ", …) — simple prefix
            // match is enough to decide which form group to highlight.
            const lbl = (item?.label || '').toLowerCase();
            if (lbl.includes('gplx')) setZoomActiveGroup('gplx');
            else if (lbl.includes('cccd') || lbl.includes('căn cước') || lbl.includes('can cuoc')) setZoomActiveGroup('cccd');
            else setZoomActiveGroup(null);
          }}
          infoPanel={displayResult ? (
            <>
              <span className="tt-zoom-info-panel__label">
                <i className="material-icons">badge</i>
                Thông tin đối chiếu · <em style={{ fontWeight: 400, marginLeft: 4 }}>sửa trực tiếp</em>
              </span>
              {/* Derive the per-field source badge from the record-level
                  scanMethod. All CCCD/GPLX fields share the same source
                  because the current pipeline groups them per document.
                  When we add per-field tracking later, each field will
                  get its own source prop instead of this uniform value. */}
              {(() => { return null; })()}
              {(() => { return null; })()}
              {(() => {
                // Per-doc source — CCCD and GPLX may come from different
                // detectors in the same record (e.g. CCCD via QR, GPLX
                // via OCR). Fall back to the record-level scanMethod when
                // the batch queue didn't stamp a specific doc source
                // (e.g. records from before this change was deployed).
                const fallback = displayResult.detectedMethod as 'qr' | 'ocr' | 'manual' | null;
                const cccdSrc = (displayResult.cccdSource ?? fallback) as 'qr' | 'ocr' | 'manual' | null;
                const gplxSrc = (displayResult.gplxSource ?? fallback) as 'qr' | 'ocr' | 'manual' | null;
                const cccdActive = zoomActiveGroup === 'cccd';
                const gplxActive = zoomActiveGroup === 'gplx';
                return (
                  <>
                    <h3 className="tt-zoom-info-panel__title">
                      <InfoField
                        label="Họ tên"
                        value={displayResult.hoTen || ''}
                        onChange={roChange((v) => handleFieldChange('hoTen', v.toUpperCase()))}
                        source={cccdSrc}
                        group="cccd"
                        highlight={cccdActive}
                        error={fieldErrors.hoTen}
                        errorKey="hoTen"
                      />
                    </h3>
                    <div className="tt-zoom-info-panel__fields">
                      {/* Order: ID fields first, then name/DOB/sex/address,
                          then dates last. Dates-last matches paper form
                          flow — operator verifies identity then dates. */}
                      <InfoField label="Số CCCD" value={displayResult.soCCCD} mono
                        source={cccdSrc} group="cccd" highlight={cccdActive}
                        error={fieldErrors.soCCCD} errorKey="soCCCD"
                        onChange={roChange((v) => handleFieldChange('soCCCD', v))} />
                      <InfoField label="Ngày sinh" value={displayResult.ngaySinh}
                        source={cccdSrc} group="cccd" highlight={cccdActive}
                        error={fieldErrors.ngaySinh} errorKey="ngaySinh"
                        onChange={roChange((v) => handleFieldChange('ngaySinh', v))} />
                      <InfoField label="Giới tính" value={displayResult.gioiTinh}
                        source={cccdSrc} group="cccd" highlight={cccdActive}
                        error={fieldErrors.gioiTinh} errorKey="gioiTinh"
                        onChange={roChange((v) => handleFieldChange('gioiTinh', v))} />
                      <InfoField label="Địa chỉ" value={displayResult.diaChi}
                        source={cccdSrc} group="cccd" highlight={cccdActive}
                        error={fieldErrors.diaChi} errorKey="diaChi"
                        onChange={roChange((v) => handleFieldChange('diaChi', v))} />
                      {(() => {
                        // Auto-converted ĐVHC cấp 2 — read-only preview.
                        // Re-runs whenever the operator edits the address
                        // above (the existing dvhc auto-convert effect
                        // watches displayResult.diaChi). Shows whether the
                        // current address resolves to a valid post-reform
                        // ĐVHC cấp 2 entry; the actual maDvhc is committed
                        // server-side on save, this row is only for the
                        // operator's eyeball check.
                        const tenDayDu = displayResult.dvhcTenDayDu || dvhcDisplayText || '';
                        const hasError = !tenDayDu && !!dvhcConvertError;
                        const valueText = dvhcConverting
                          ? 'Đang chuyển đổi…'
                          : tenDayDu || (dvhcConvertError ? '' : '— chưa có —');
                        return (
                          <div
                            className={
                              `tt-zoom-info-panel__field tt-zoom-info-panel__field--cccd`
                              + (hasError ? ' tt-zoom-info-panel__field--warning' : '')
                            }
                            style={hasError ? { borderColor: '#c62828', background: '#fde7e7' } : undefined}
                            title="ĐVHC cấp 2 tự chuyển đổi từ địa chỉ. Chỉ để xem, không chỉnh sửa được."
                          >
                            <span className="tt-zoom-info-panel__field-label">
                              ĐVHC cấp 2
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 3,
                                  marginLeft: 6,
                                  padding: '1px 6px',
                                  borderRadius: 3,
                                  fontSize: '0.6rem',
                                  fontWeight: 600,
                                  background: '#e0e0e0',
                                  color: '#424242',
                                  textTransform: 'uppercase',
                                }}
                              >
                                <i className="material-icons" style={{ fontSize: 9 }}>lock</i> auto
                              </span>
                            </span>
                            <span
                              className="tt-zoom-info-panel__field-value"
                              style={hasError ? { color: '#c62828', fontWeight: 600 } : undefined}
                            >
                              {valueText}
                              {hasError && (
                                <span style={{ marginLeft: 8, fontSize: '0.72rem', fontWeight: 500 }}>
                                  ⚠ {dvhcConvertError}
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })()}
                      <InfoField label="Ngày cấp CCCD" value={displayResult.ngayCap}
                        source={cccdSrc} group="cccd" highlight={cccdActive}
                        error={fieldErrors.ngayCap} errorKey="ngayCap"
                        onChange={roChange((v) => handleFieldChange('ngayCap', v))} />
                      <div style={{ margin: '8px 0', borderTop: '1px solid #e0e0e0' }} />
                      <InfoField label="Số GPLX" value={displayResult.soGPLX} mono
                        source={gplxSrc} group="gplx" highlight={gplxActive}
                        error={fieldErrors.soGPLX} errorKey="soGPLX"
                        onChange={roChange((v) => handleFieldChange('soGPLX', v))} />
                      <InfoField label="Hạng GPLX" value={displayResult.hangGPLX}
                        source={gplxSrc} group="gplx" highlight={gplxActive}
                        error={fieldErrors.hangGPLX} errorKey="hangGPLX"
                        onChange={roChange((v) => handleFieldChange('hangGPLX', v))} />
                      <InfoField label="Ngày cấp GPLX" value={displayResult.ngayCapGPLX}
                        source={gplxSrc} group="gplx" highlight={gplxActive}
                        error={fieldErrors.ngayCapGPLX} errorKey="ngayCapGPLX"
                        onChange={roChange((v) => handleFieldChange('ngayCapGPLX', v))} />
                      <InfoField label="Ngày hết hạn GPLX" value={displayResult.ngayHetHanGPLX}
                        source={gplxSrc} group="gplx" highlight={gplxActive}
                        error={fieldErrors.ngayHetHanGPLX} errorKey="ngayHetHanGPLX"
                        onChange={roChange((v) => handleFieldChange('ngayHetHanGPLX', v))} />
                      <InfoField label="Ngày trúng tuyển" value={displayResult.ngayTrungTuyenGPLX}
                        source={gplxSrc} group="gplx" highlight={gplxActive}
                        error={fieldErrors.ngayTrungTuyenGPLX} errorKey="ngayTrungTuyenGPLX"
                        onChange={roChange((v) => handleFieldChange('ngayTrungTuyenGPLX', v))} />
                      {(() => {
                        // Auto-derived Số năm lái xe. Read-only; the only
                        // input is ngayTrungTuyen above. Red warning when
                        // < 3 years (common "new driver" threshold).
                        const d = calcDrivingYears(displayResult.ngayTrungTuyenGPLX);
                        const val = d ? `${Math.floor(d.years)} năm` : '— chưa có —';
                        return (
                          <div
                            className={
                              `tt-zoom-info-panel__field tt-zoom-info-panel__field--gplx` +
                              (d?.warning ? ' tt-zoom-info-panel__field--warning' : '')
                            }
                            style={d?.warning ? { borderColor: '#c62828', background: '#fde7e7' } : undefined}
                            title="Tự tính từ ngày trúng tuyển GPLX. Không chỉnh sửa được trực tiếp."
                          >
                            <span className="tt-zoom-info-panel__field-label">
                              Số năm lái xe
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 3,
                                  marginLeft: 6,
                                  padding: '1px 6px',
                                  borderRadius: 3,
                                  fontSize: '0.6rem',
                                  fontWeight: 600,
                                  background: '#e0e0e0',
                                  color: '#424242',
                                  textTransform: 'uppercase',
                                }}
                              >
                                <i className="material-icons" style={{ fontSize: 9 }}>lock</i> auto
                              </span>
                            </span>
                            <span
                              className="tt-zoom-info-panel__field-value"
                              style={d?.warning ? { color: '#c62828', fontWeight: 700 } : undefined}
                            >
                              {val}
                              {d?.warning && (
                                <span style={{ marginLeft: 8, fontSize: '0.7rem', fontWeight: 600 }}>
                                  ⚠ Chưa đủ 3 năm
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </>
                );
              })()}
            </>
          ) : undefined}
        />
        );
      })()}

      {/* Magnify tooltip — lifted above the hovered field so the
          original text remains visible + copyable underneath. */}
      <div
        className={`tt-magnify-tip ${tipVisible ? 'tt-magnify-tip--visible' : ''}`}
        style={{ left: tipPos.x, top: tipPos.y, transform: 'translateY(-100%)' }}
      >
        {tipText}
      </div>

      {/* Batch thumbnail hover preview — large name + avatar so the user
          can identify a record without clicking. */}
      {thumbHover && batchItems[thumbHover.idx] && (() => {
        const it = batchItems[thumbHover.idx];
        const name = it.result?.hoTen || it.fileName;
        const cccd = it.result?.soCCCD || '';
        const previewSrc = it.result?.avatarBase64 || it.result?.sourceImageBase64 || it.preview;
        return (
          <div
            className="tt-scanner__batch-tip"
            style={{ left: thumbHover.x, top: thumbHover.y }}
          >
            <img src={previewSrc} alt="" className="tt-scanner__batch-tip-img" />
            <div className="tt-scanner__batch-tip-body">
              <div className="tt-scanner__batch-tip-name">{name}</div>
              {cccd && <div className="tt-scanner__batch-tip-cccd">{cccd}</div>}
              <div className={`tt-scanner__batch-tip-status tt-scanner__batch-tip-status--${it.status}`}>
                {it.status === 'done' && <><i className="material-icons">check_circle</i> Đã quét</>}
                {it.status === 'error' && <><i className="material-icons">error</i> Lỗi quét</>}
                {it.status === 'processing' && <><span className="tt-spinner" /> Đang xử lý</>}
                {it.status === 'pending' && <><i className="material-icons">schedule</i> Đang chờ</>}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default TtScannerForm;
